import React, { useEffect, useMemo, useRef, useState } from "react";
import { getProductos } from "../helpers/rutaProductos";
import {
  getUsuariosColaboradores,
} from "../helpers/rutaUsuarios";
import {
  postCliente,
  buscarClientes,
  subirDocumentoCliente,
  getClientes,
} from "../helpers/rutaClientes";
import { getFinancieras } from "../helpers/rutaFinancieras";
import {
  postPagoVenta,
  MEDIOS_PAGO_VENTA,
} from "../helpers/rutaPagoVentas";
import {
  postClienteRecepcion,
  buscarClientesRecepcion,
  subirDocumentoClienteRecepcion,
} from "../helpers/rutaClienterecepcion";
import { postVenta } from "../helpers/rutaVentas";
import {
  leerDocumentoClienteOCR,
  mapOcrAClienteCompra,
} from "../helpers/rutaOcrDocumento";
import ModalGaranteFinanciacion from "./ModalGaranteFinanciacion";
import ModalDocumentoCropper from "./ModalDocumentoCropper";
import CalculadoraRiders from "./CalculadoraRiders";
import "../css/venta-rapida-wizard.css";


import logoAcima from "../assets/financieras/acima.jpeg";
import logoAff from "../assets/financieras/aff.jpeg";
import logoAffirm from "../assets/financieras/affirm.jpg";
import logoKatapult from "../assets/financieras/katapult.jpg";
import logoSnap from "../assets/financieras/snap.jpeg";
import logoSynchrony from "../assets/financieras/synchrony.jpg";
import logoUsbank from "../assets/financieras/usbank.jpeg";
/* ----------------------------- UTILIDADES ----------------------------- */

const clean = (v) => String(v ?? "").trim();
const upper = (v) => clean(v).toUpperCase();
const upperTyping = (v) => String(v ?? "").toUpperCase();
const lower = (v) => clean(v).toLowerCase();

const onlyDigits = (v) => String(v ?? "").replace(/\D/g, "");

const limitDigits = (v, max) => onlyDigits(v).slice(0, max);



/* =========================================================
   TELÉFONO USA
   Visual:   +1 (786)-365-3986
   Guardado: +17863653986
========================================================= */

const getTelefonoUSADigitos = (v = "") => {
  const texto = String(v ?? "").trim();

  let digits = onlyDigits(texto);

  /*
    Quitamos el código de país solamente cuando:
    - viene explícitamente como +1...
    - o llegan 11 dígitos comenzando en 1
  */
  const tienePrefijoMasUno =
    /^\+1/.test(texto);

  if (
    digits.startsWith("1") &&
    (
      tienePrefijoMasUno ||
      digits.length === 11
    )
  ) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const normalizarTelefonoUSA = (v = "") => {
  const digits =
    getTelefonoUSADigitos(v);

  if (!digits) {
    return "+1";
  }

  return `+1${digits}`;
};

const telefonoUSAContinuo = (v = "") => {
  const digits =
    getTelefonoUSADigitos(v);

  if (!digits) {
    return "";
  }

  return `+1${digits}`;
};

const formatearTelefonoUSA = (v = "") => {
  const digits =
    getTelefonoUSADigitos(v);

  if (!digits) {
    return "+1 ";
  }

  if (digits.length <= 3) {
    return `+1 (${digits}`;
  }

  if (digits.length <= 6) {
    return (
      `+1 (${digits.slice(0, 3)})-` +
      digits.slice(3)
    );
  }

  return (
    `+1 (${digits.slice(0, 3)})-` +
    `${digits.slice(3, 6)}-` +
    digits.slice(6, 10)
  );
};

const copiarAlPortapapeles = async (texto = "") => {
  if (
    navigator.clipboard &&
    window.isSecureContext
  ) {
    await navigator.clipboard.writeText(
      texto
    );

    return;
  }

  /*
    Respaldo para navegadores donde
    navigator.clipboard no esté disponible.
  */
  const textarea =
    document.createElement("textarea");

  textarea.value = texto;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";

  document.body.appendChild(
    textarea
  );

  textarea.focus();
  textarea.select();

  document.execCommand("copy");

  document.body.removeChild(
    textarea
  );
};

const normalizarTelefonoCuba = (v = "") => {
  const digits = onlyDigits(v).slice(0, 10);

  if (!digits) return "";

  return `+${digits}`;
};

const esTelefonoCubaValido = (v = "") => {
  return /^\+\d{10}$/.test(String(v || "").trim());
};

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "object") return clean(v._id ?? v.id ?? v.value ?? "");
  return clean(v);
};

const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const formatUSD = (v) => {
  const n = Number(v || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const logosFinancieras = {
  ACIMA: logoAcima,
  AFF: logoAff,
  "AMERICAN FIRST FINANCE": logoAff,
  AFFIRM: logoAffirm,
  KATAPULT: logoKatapult,
  SNAP: logoSnap,

  SYNCHRONY: logoSynchrony,
  "SYNCHRONY BANK": logoSynchrony,
  SYNCRONY: logoSynchrony,
  "SYNCRONY BANK": logoSynchrony,

  USBANK: logoUsbank,
  "US BANK": logoUsbank,

  "USBANK CARITAS": logoUsbank,
  "US BANK CARITAS": logoUsbank,

  "USBANK EVERLY MARKET": logoUsbank,
  "US BANK EVERLY MARKET": logoUsbank,

};

const getNombreFinancieraLogo = (f = {}) =>
  upper(f.financiera || f.razonsocial || f.nombre || "");

const getLogoFinanciera = (financieraId, financieras = []) => {
  const f = financieras.find((x) => getId(x) === getId(financieraId));

  if (!f) return "";

  // 1. Logo cargado dinámicamente desde Administración
  const logoDinamico = clean(f?.logo?.url);

  if (logoDinamico) {
    return logoDinamico;
  }

  // 2. Logo viejo local como respaldo
  const nombre = getNombreFinancieraLogo(f);

  return logosFinancieras[nombre] || "";
};

const normalizarNombreFinanciera = (v = "") =>
  String(v || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");

const getFinancieraById = (financieraId, financieras = []) => {
  const id = getId(financieraId);

  if (!id) return null;

  return financieras.find((f) => getId(f) === id) || null;
};

const getNombreFinancieraPago = (pago = {}, financieras = []) => {
  const f = getFinancieraById(pago.financiera, financieras);

  return normalizarNombreFinanciera(
    f?.financiera || f?.razonsocial || f?.nombre || ""
  );
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const TAX_PORCENTAJE = 7;

const esEfectivoEnTienda = (medioPago = "") =>
  upper(medioPago) === "EFECTIVO EN TIENDA";

const LIMITE_FINANCIAMIENTO_CASA_USD = 1000;

const esFinanciamientoCasa = (medioPago = "") =>
  upper(medioPago) ===
  "FINANCIAMIENTO CON LA CASA";

const FINANCIERAS_SIN_TAX = new Set([
  "LENDMARK - VIP WHEELS",
  "SNAP - VIP WHEELS",
  "PROGRESSIVE - VIP WHEELS",
  "KOALA-FI - VIP WHEELS",
  "KAFENE - VIP WHEELS",
  "EASYPAY - VIP WHEELS",
  "KIWI PAY - VIP WHEELS",
]);

const esFinancieraSinTax = (
  pago = {},
  financieras = []
) => {
  if (
    upper(pago?.medioPago) !==
    "CREDITO FINANCIERA"
  ) {
    return false;
  }

  const financiera =
    getFinancieraById(
      pago.financiera,
      financieras
    );

  /*
    NUEVA CONFIGURACIÓN.

    Si la financiera fue activada desde
    Administración, usamos aplicaTax.
  */
  if (
    financiera
      ?.configuracionPagosActiva ===
    true
  ) {
    return financiera.aplicaTax ===
      false;
  }

  /*
    CONFIGURACIÓN HISTÓRICA.

    Mientras la nueva configuración
    no esté activa, seguimos usando
    exactamente la tabla anterior.
  */
  const nombre =
    getNombreFinancieraPago(
      pago,
      financieras
    );

  return FINANCIERAS_SIN_TAX.has(
    nombre
  );
};

const esPagoSinTax = (
  pago = {},
  financieras = []
) =>
  esEfectivoEnTienda(
    pago?.medioPago
  ) ||
  esFinanciamientoCasa(
    pago?.medioPago
  ) ||
  esFinancieraSinTax(
    pago,
    financieras
  );

const esCreditoTarjeta = (medioPago = "") =>
  upper(medioPago) === "CREDITO";

const esDebitoTarjeta = (medioPago = "") =>
  upper(medioPago) === "DEBITO";

const permiteResponsablePago = (medioPago = "") => {
  const medio = upper(medioPago);

  return [
    "CREDITO FINANCIERA",
    "CREDITO",
    "DEBITO",
  ].includes(medio);
};

/*
  NO PAGAN TAX:

  - EFECTIVO EN TIENDA
  - determinadas financieras VIP WHEELS.

  El importe ingresado representa el monto
  real aplicado a la venta.

  El Tax correspondiente a ese importe
  se descuenta del Tax total de la venta.
*/
const calcularPagosConDevolucionTax = (
  pagos = [],
  taxMaximoVentaUSD = 0,
  financieras = [],
  totalVentaConTaxUSD = 0
) => {
  const taxMaximoUSD = round2(
    Math.max(
      0,
      toNumber(
        taxMaximoVentaUSD,
        0
      )
    )
  );

  const totalVentaUSD = round2(
    Math.max(
      0,
      toNumber(
        totalVentaConTaxUSD,
        0
      )
    )
  );

  /*
    Primero calculamos cada pago
    con su valor financiero nominal.

    Para FONDEO_INVERSO, inicialmente
    el valor nominal es:

    fondeo - fee
  */
  const detallesBase =
    pagos.map((pago) => {
      const feePreview =
        calcularFeePagoRapido(
          pago,
          financieras
        );

      const montoNominalUSD =
        round2(
          Math.max(
            0,
            toNumber(
              feePreview
                ?.montoAplicadoUSD,
              0
            )
          )
        );

      return {
        pago,

        feePreview,

        montoNominalUSD,

        pagoSinTax:
          esPagoSinTax(
            pago,
            financieras
          ),
      };
    });

  /*
    Si existe FONDEO_INVERSO,
    el último pago de este tipo
    se utiliza para cerrar exactamente
    el saldo real de la venta.

    Esto permite que la financiera
    informe 6019, 6190, 6008, etc.,
    sin deformar el total de la venta.
  */
  let indiceFondeoAjuste =
    -1;

  detallesBase.forEach(
    (detalle, idx) => {
      if (
        detalle.feePreview
          ?.esFondeoInverso &&
        toNumber(
          detalle.feePreview
            ?.fondeoFinancieraUSD,
          0
        ) > 0
      ) {
        indiceFondeoAjuste =
          idx;
      }
    }
  );

  if (
    indiceFondeoAjuste >= 0 &&
    totalVentaUSD > 0
  ) {
    let totalOtrosPagosUSD =
      0;

    let taxPotencialOtrosUSD =
      0;

    detallesBase.forEach(
      (detalle, idx) => {
        if (
          idx ===
          indiceFondeoAjuste
        ) {
          return;
        }

        totalOtrosPagosUSD =
          round2(
            totalOtrosPagosUSD +
              detalle.montoNominalUSD
          );

        if (
          detalle.pagoSinTax &&
          detalle.montoNominalUSD >
            0
        ) {
          taxPotencialOtrosUSD =
            round2(
              taxPotencialOtrosUSD +
                round2(
                  detalle.montoNominalUSD *
                    (
                      TAX_PORCENTAJE /
                      100
                    )
                )
            );
        }
      }
    );

    const devolucionOtrosUSD =
      round2(
        Math.min(
          taxPotencialOtrosUSD,
          taxMaximoUSD
        )
      );

    const taxDisponibleParaFondeoUSD =
      round2(
        Math.max(
          taxMaximoUSD -
            devolucionOtrosUSD,
          0
        )
      );

    /*
      Importe que queda por cubrir
      antes de considerar el posible
      beneficio de Tax de este pago.
    */
    const saldoParaFondeoUSD =
      round2(
        Math.max(
          totalVentaUSD -
            devolucionOtrosUSD -
            totalOtrosPagosUSD,
          0
        )
      );

    const detalleFondeo =
      detallesBase[
        indiceFondeoAjuste
      ];

    let montoObjetivoUSD =
      saldoParaFondeoUSD;

    /*
      Si esta financiera NO lleva Tax,
      resolvemos también el Tax para
      obtener el monto exacto que debe
      aplicarse a la venta.
    */
    if (
      detalleFondeo.pagoSinTax &&
      saldoParaFondeoUSD > 0
    ) {
      const candidatoUSD =
        round2(
          saldoParaFondeoUSD /
            (
              1 +
              TAX_PORCENTAJE /
                100
            )
        );

      const taxCandidatoUSD =
        round2(
          candidatoUSD *
            (
              TAX_PORCENTAJE /
              100
            )
        );

      montoObjetivoUSD =
        taxCandidatoUSD <=
        taxDisponibleParaFondeoUSD +
          0.01
          ? candidatoUSD
          : round2(
              Math.max(
                saldoParaFondeoUSD -
                  taxDisponibleParaFondeoUSD,
                0
              )
            );
    }

    detalleFondeo.montoNominalUSD =
      montoObjetivoUSD;

    /*
      Recalculamos el detalle financiero
      usando el monto que REALMENTE
      aplicaremos a la venta.

      El fondeo informado no cambia.
    */
    detalleFondeo.feePreview =
      calcularFeePagoRapido(
        {
          ...detalleFondeo.pago,

          montoAplicadoForzadoUSD:
            montoObjetivoUSD,
        },
        financieras
      );
  }

  /*
    Segunda pasada:
    calculamos Tax y monto aplicado
    definitivo de cada pago.
  */
  let taxDisponibleUSD =
    taxMaximoUSD;

  return detallesBase.map(
    (detalle) => {
      const {
        pago,
        feePreview,
        pagoSinTax,
      } = detalle;

      const montoRealUSD =
        round2(
          Math.max(
            0,
            toNumber(
              detalle.montoNominalUSD,
              0
            )
          )
        );

        let devolucionTaxUSD = 0;

        if (
          pagoSinTax &&
          montoRealUSD > 0 &&
          taxDisponibleUSD > 0
        ) {
          const devolucionCalculadaUSD =
            round2(
              montoRealUSD *
                (
                  TAX_PORCENTAJE /
                  100
                )
            );

          devolucionTaxUSD =
            round2(
              Math.min(
                devolucionCalculadaUSD,
                taxDisponibleUSD
              )
            );

          taxDisponibleUSD =
            round2(
              Math.max(
                0,
                taxDisponibleUSD -
                  devolucionTaxUSD
              )
            );
        }

        /*
          Tax efectivamente cobrado en este pago.
          Si el medio no lleva Tax, es cero.
        */
        const taxCobradoUSD =
          !pagoSinTax &&
          montoRealUSD > 0
            ? round2(
                montoRealUSD *
                  (
                    TAX_PORCENTAJE /
                    (100 + TAX_PORCENTAJE)
                  )
              )
            : 0;

      return {
        pago,

        feePreview,

        montoRealUSD,

        devolucionTaxUSD,

        taxCobradoUSD,

        montoAplicadoUSD:
          montoRealUSD,

        pagoSinTax,
      };
    }
  );
};

const calcularFeePagoRapido = (
  pago = {},
  financieras = []
) => {
  const medio =
    upper(pago.medioPago);

  const montoUSD =
    round2(
      Math.max(
        0,
        toNumber(
          pago.montoUSD,
          0
        )
      )
    );

  /*
    ======================================================
    TARJETA DE CRÉDITO
    ======================================================
  */
  if (medio === "CREDITO") {
    const feePorcentaje = 3;

    const feeMontoUSD =
      round2(
        montoUSD *
          (
            feePorcentaje /
            100
          )
      );

    const totalCobrarClienteUSD =
      round2(
        montoUSD +
          feeMontoUSD
      );

    return {
      configuracionNueva:
        false,

      tipoCalculo:
        "MONTO_DIRECTO",

      esFondeoInverso:
        false,

      requiereCuotas:
        false,

      cuotas:
        0,

      cuotasDisponibles:
        [],

      feePorcentaje,
      feeMontoUSD,

      fondeoFinancieraUSD:
        0,

      montoAplicadoUSD:
        montoUSD,

      montoFinancieraUSD:
        montoUSD,

      totalCobrarClienteUSD,
    };
  }

  /*
    ======================================================
    EFECTIVO / ZELLE / DÉBITO
    ======================================================
  */
  if (
    medio !==
    "CREDITO FINANCIERA"
  ) {
    return {
      configuracionNueva:
        false,

      tipoCalculo:
        "MONTO_DIRECTO",

      esFondeoInverso:
        false,

      requiereCuotas:
        false,

      cuotas:
        0,

      cuotasDisponibles:
        [],

      feePorcentaje:
        0,

      feeMontoUSD:
        0,

      fondeoFinancieraUSD:
        0,

      montoAplicadoUSD:
        montoUSD,

      montoFinancieraUSD:
        montoUSD,

      totalCobrarClienteUSD:
        montoUSD,
    };
  }

  /*
    ======================================================
    FINANCIERA SELECCIONADA
    ======================================================
  */

  const financiera =
    getFinancieraById(
      pago.financiera,
      financieras
    );

  const configuracionNueva =
    financiera
      ?.configuracionPagosActiva ===
    true;

  const tipoCalculo =
    upper(
      financiera?.tipoCalculo ||
        "GROSS_UP"
    );

  const cuotas =
    toNumber(
      pago.cuotasFinanciacion,
      0
    );

  /*
    ======================================================
    NUEVA CONFIGURACIÓN DINÁMICA
    ======================================================
  */

  if (configuracionNueva) {
    const reglas =
      Array.isArray(
        financiera?.reglasCuotas
      )
        ? financiera.reglasCuotas
        : [];

    const requiereCuotas =
      Boolean(
        financiera
          ?.requiereCuotas
      ) ||
      tipoCalculo ===
        "POR_CUOTA";

    const cuotasDisponibles =
      reglas
        .map((regla) =>
          Number(
            regla?.cuotas
          )
        )
        .filter(
          (n) =>
            Number.isFinite(n) &&
            n > 0
        );

    let feePorcentaje =
      Math.max(
        0,
        toNumber(
          financiera
            ?.feePorcentaje,
          0
        )
      );

    /*
      Si tiene reglas por cuotas,
      el fee de la regla reemplaza
      al fee general.
    */
    if (
      requiereCuotas &&
      cuotas > 0
    ) {
      const regla =
        reglas.find(
          (r) =>
            Number(
              r?.cuotas
            ) === cuotas
        );

      if (regla) {
        feePorcentaje =
          Math.max(
            0,
            toNumber(
              regla
                ?.feePorcentaje,
              0
            )
          );
      }
    }

    /*
      ====================================================
      FONDEO INVERSO

      Ejemplo:
      Fondeo 6018.75
      Fee 4% = 240.75
      Aplicado = 5778
      ====================================================
    */
    if (
      tipoCalculo ===
      "FONDEO_INVERSO"
    ) {
      const fondeoFinancieraUSD =
        round2(
          Math.max(
            0,
            toNumber(
              pago
                .fondeoFinancieraUSD,
              0
            )
          )
        );

      const feeMontoUSD =
        round2(
          fondeoFinancieraUSD *
            (
              feePorcentaje /
              100
            )
        );

      /*
        Neto REAL de la financiera.
      */
      const montoNetoFinancieraUSD =
        round2(
          Math.max(
            fondeoFinancieraUSD -
              feeMontoUSD,
            0
          )
        );

      /*
        En la primera pasada no existe
        montoAplicadoForzadoUSD y usamos
        el neto financiero.

        Después, el resumen puede indicar
        cuánto debe aplicarse realmente
        para cerrar la venta.
      */
      const tieneMontoForzado =
        Object.prototype.hasOwnProperty.call(
          pago,
          "montoAplicadoForzadoUSD"
        );

      const montoForzadoUSD =
        round2(
          Math.max(
            0,
            toNumber(
              pago
                .montoAplicadoForzadoUSD,
              0
            )
          )
        );

      const montoAplicadoUSD =
        tieneMontoForzado
          ? montoForzadoUSD
          : montoNetoFinancieraUSD;

      const factorNeto =
        1 -
        feePorcentaje / 100;

      /*
        Cuánto debería haber fondeado
        correctamente la financiera.
      */
      const fondeoEsperadoUSD =
        factorNeto > 0
          ? round2(
              montoAplicadoUSD /
                factorNeto
            )
          : montoAplicadoUSD;

      /*
        Diferencia entre el dinero neto
        real de la financiera y lo que
        aplicamos a la venta.

        + = excedente
        - = faltante
      */
      const diferenciaFinancieraUSD =
        round2(
          montoNetoFinancieraUSD -
            montoAplicadoUSD
        );

      /*
        Si falta dinero, queda registrado
        como ajuste financiero.
      */
      const ajusteFinancieroUSD =
        round2(
          Math.max(
            montoAplicadoUSD -
              montoNetoFinancieraUSD,
            0
          )
        );

      return {
        configuracionNueva:
          true,

        tipoCalculo,

        esFondeoInverso:
          true,

        requiereCuotas,

        cuotas,

        cuotasDisponibles,

        feePorcentaje,

        feeMontoUSD,

        fondeoFinancieraUSD,

        fondeoEsperadoUSD,

        diferenciaFinancieraUSD,

        ajusteFinancieroUSD,

        montoAplicadoUSD,

        /*
          Neto REAL financiero.
        */
        montoFinancieraUSD:
          montoNetoFinancieraUSD,

        totalCobrarClienteUSD:
          fondeoFinancieraUSD,
      };
    }

    /*
      ====================================================
      MONTO DIRECTO
      ====================================================
    */
    if (
      tipoCalculo ===
      "MONTO_DIRECTO"
    ) {
      const feeMontoUSD =
        round2(
          montoUSD *
            (
              feePorcentaje /
              100
            )
        );

      return {
        configuracionNueva:
          true,

        tipoCalculo,

        esFondeoInverso:
          false,

        requiereCuotas,

        cuotas,

        cuotasDisponibles,

        feePorcentaje,

        feeMontoUSD,

        fondeoFinancieraUSD:
          0,

        montoAplicadoUSD:
          montoUSD,

        montoFinancieraUSD:
          montoUSD,

        totalCobrarClienteUSD:
          round2(
            montoUSD +
              feeMontoUSD
          ),
      };
    }

    /*
      ====================================================
      GROSS UP / POR CUOTA
      BIDIRECCIONAL
      ====================================================
    */

    const factorNeto =
      1 -
      feePorcentaje / 100;

    const usaTotalCobradoManual =
      pago.origenCalculoFinanciera ===
        "TOTAL_COBRADO" &&
      toNumber(
        pago.totalCobradoManualUSD,
        0
      ) > 0;

    const totalManualUSD =
      round2(
        Math.max(
          0,
          toNumber(
            pago.totalCobradoManualUSD,
            0
          )
        )
      );

    const montoAplicadoUSD =
      usaTotalCobradoManual
        ? factorNeto > 0
          ? round2(
              totalManualUSD *
                factorNeto
            )
          : totalManualUSD
        : montoUSD;

    const totalCobrarClienteUSD =
      usaTotalCobradoManual
        ? totalManualUSD
        : feePorcentaje > 0 &&
          factorNeto > 0
        ? round2(
            montoAplicadoUSD /
              factorNeto
          )
        : montoAplicadoUSD;

    const feeMontoUSD =
      round2(
        totalCobrarClienteUSD -
          montoAplicadoUSD
      );

    return {
      configuracionNueva:
        true,

      tipoCalculo,

      esFondeoInverso:
        false,

      requiereCuotas,

      cuotas,

      cuotasDisponibles,

      feePorcentaje,

      feeMontoUSD,

      fondeoFinancieraUSD:
        0,

      montoAplicadoUSD,

      montoFinancieraUSD:
        montoAplicadoUSD,

      totalCobrarClienteUSD,
    };
  }

  /*
    ======================================================
    LÓGICA HISTÓRICA

    NO TOCAR mientras
    configuracionPagosActiva = false
    ======================================================
  */

  const nombre =
    getNombreFinancieraPago(
      pago,
      financieras
    );

  let feePorcentaje = 0;

  let requiereCuotas =
    false;

  let cuotasDisponibles =
    [];

  if (
    nombre.includes(
      "ACIMA"
    )
  ) {
    feePorcentaje = 0;
  } else if (
    nombre ===
    "AFF - VIP WHEELS"
  ) {
    feePorcentaje = 15;
  } else if (
    nombre ===
    "KIWI PAY - VIP WHEELS"
  ) {
    feePorcentaje = 18;
  } else if (
    [
      "LENDMARK - VIP WHEELS",
      "SNAP - VIP WHEELS",
      "PROGRESSIVE - VIP WHEELS",
      "KOALA-FI - VIP WHEELS",
      "KAFENE - VIP WHEELS",
      "EASYPAY - VIP WHEELS",
    ].includes(nombre)
  ) {
    feePorcentaje = 10;
  } else if (
    nombre.includes(
      "SNAP"
    )
  ) {
    feePorcentaje = 3;
  } else if (
    nombre.includes(
      "SYNCHRONY"
    ) ||
    nombre.includes(
      "SYNCRONY"
    ) ||
    nombre.includes(
      "SINCRONIA"
    ) ||
    nombre.includes(
      "SINCRON"
    )
  ) {
    requiereCuotas = true;

    cuotasDisponibles = [
      12,
      18,
      24,
    ];

    if (cuotas === 12) {
      feePorcentaje = 3;
    } else if (
      cuotas === 18
    ) {
      feePorcentaje = 6;
    } else if (
      cuotas === 24
    ) {
      feePorcentaje = 9;
    }
  } else if (
    (
      nombre.includes(
        "USBANK"
      ) ||
      nombre.includes(
        "US BANK"
      )
    ) &&
    nombre.includes(
      "EVERLY"
    )
  ) {
    feePorcentaje = 8;
  } else if (
    (
      nombre.includes(
        "USBANK"
      ) ||
      nombre.includes(
        "US BANK"
      )
    ) &&
    nombre.includes(
      "CARITAS"
    )
  ) {
    feePorcentaje = 4;
  } else if (
    nombre.includes(
      "USBANK"
    ) ||
    nombre.includes(
      "US BANK"
    )
  ) {
    feePorcentaje = 4;
  }

  const factorNeto =
    1 -
    feePorcentaje / 100;

  /*
    GROSS UP HISTÓRICO BIDIRECCIONAL

    Si escribe MONTO APLICADO:
    calculamos TOTAL COBRADO.

    Si escribe TOTAL COBRADO:
    calculamos MONTO APLICADO.
  */
  const usaTotalCobradoManual =
    pago.origenCalculoFinanciera ===
      "TOTAL_COBRADO" &&
    toNumber(
      pago.totalCobradoManualUSD,
      0
    ) > 0;

  const totalManualUSD =
    round2(
      Math.max(
        0,
        toNumber(
          pago.totalCobradoManualUSD,
          0
        )
      )
    );

  const montoAplicadoUSD =
    usaTotalCobradoManual
      ? factorNeto > 0
        ? round2(
            totalManualUSD *
              factorNeto
          )
        : totalManualUSD
      : montoUSD;

  const totalCobrarClienteUSD =
    usaTotalCobradoManual
      ? totalManualUSD
      : feePorcentaje > 0 &&
        factorNeto > 0
      ? round2(
          montoAplicadoUSD /
            factorNeto
        )
      : montoAplicadoUSD;

  const feeMontoUSD =
    round2(
      totalCobrarClienteUSD -
        montoAplicadoUSD
    );

  return {
    configuracionNueva:
      false,

    tipoCalculo:
      "GROSS_UP",

    esFondeoInverso:
      false,

    requiereCuotas,

    cuotas,

    cuotasDisponibles,

    feePorcentaje,

    feeMontoUSD,

    fondeoFinancieraUSD:
      0,

    montoAplicadoUSD,

    montoFinancieraUSD:
      montoAplicadoUSD,

    totalCobrarClienteUSD,
  };
};

const fechaHoyArgentina = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const get = (type) => parts.find((p) => p.type === type)?.value || "";

  return `${get("year")}-${get("month")}-${get("day")}`;
};

const makeTempId = (prefix = "tmp") =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const unwrapProductos = (r) => {
  if (Array.isArray(r?.productos)) return r.productos;
  if (Array.isArray(r?.data?.productos)) return r.data.productos;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const unwrapClienteGuardado = (resp) => {
  return resp?.cliente || resp?.data?.cliente || resp?.data || resp || null;
};

const unwrapReceptorGuardado = (resp) => {
  return (
    resp?.clienterecepcion ||
    resp?.cliente ||
    resp?.data?.clienterecepcion ||
    resp?.data?.cliente ||
    resp?.data ||
    resp ||
    null
  );
};

const nombreProducto = (p = {}) => {
  return (
    clean(p?.nombreComercial) ||
    clean(p?.modelo) ||
    clean(p?.descripcion) ||
    "-"
  );
};

const getProductoPrecio = (p = {}) => toNumber(p?.precioVentaUSD, 0);
const getProductoEnvio = (p = {}) => toNumber(p?.costoEnvioUSD, 0);

const getColorId = (color) => getId(color);

const getColorNombre = (color = {}) =>
  clean(color?.nombre) ||
  clean(color?.color) ||
  clean(color?.descripcion) ||
  clean(color?.codigoHex) ||
  "SIN NOMBRE";

const normalizarTexto = (valor = "") =>
  String(valor || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const CUBA_PROVINCIAS = [
  "Pinar del Río",
  "Artemisa",
  "La Habana",
  "Mayabeque",
  "Matanzas",
  "Cienfuegos",
  "Villa Clara",
  "Sancti Spíritus",
  "Ciego de Ávila",
  "Camagüey",
  "Las Tunas",
  "Holguín",
  "Granma",
  "Santiago de Cuba",
  "Guantánamo",
];

const obtenerZonaLogisticaPorProvincia = (provincia = "") => {
  const p = normalizarTexto(provincia);

  const mapa = {
    "LA HABANA": "PUERTO DEL MARIEL",
    "PINAR DEL RIO": "PUERTO DEL MARIEL",
    ARTEMISA: "PUERTO DEL MARIEL",
    MAYABEQUE: "PUERTO DEL MARIEL",
    MATANZAS: "PUERTO DEL MARIEL",

    CIENFUEGOS: "ZONA CENTRAL",
    "VILLA CLARA": "ZONA CENTRAL",
    "SANCTI SPIRITUS": "ZONA CENTRAL",
    "CIEGO DE AVILA": "ZONA CENTRAL",
    CAMAGUEY: "ZONA CENTRAL",
    "LAS TUNAS": "ZONA CENTRAL",

    "SANTIAGO DE CUBA": "SANTIAGO DE CUBA",
    GRANMA: "SANTIAGO DE CUBA",
    HOLGUIN: "SANTIAGO DE CUBA",
    GUANTANAMO: "SANTIAGO DE CUBA",
  };

  return mapa[p] || "";
};

const buildNombreCompleto = ({
  nombre,
  segundoNombre,
  apellido1,
  apellido2,
}) =>
  [upper(nombre), upper(segundoNombre), upper(apellido1), upper(apellido2)]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const buildDireccionCompleta = ({
  direccion,
  referenciaDireccion,
  municipio,
  provincia,
  pais,
}) => {
  const dir = upper(direccion);
  const ref = upper(referenciaDireccion);

  const partes = [
    dir,
    ref && normalizarTexto(ref) !== normalizarTexto(dir) ? ref : "",
    upper(municipio),
    upper(provincia),
    upper(pais) || "CUBA",
  ];

  return partes
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();
};

const nombreReceptorSelect = (receptor = {}, idx = 0) => {
  const nombreCompleto = buildNombreCompleto(receptor);

  return (
    nombreCompleto ||
    upper(receptor.carnetIdentidad) ||
    `RECEPTOR ${idx + 1}`
  );
};

const normalizarTelefonoWhatsApp = (telefono = "") => {
  let n = String(telefono || "").replace(/\D/g, "");

  if (!n) return "";

  // Si viene tipo 0053...
  if (n.startsWith("00")) {
    n = n.slice(2);
  }

  // Si viene tipo 053...
  if (n.startsWith("053")) {
    n = n.slice(1);
  }

  // Cuba: si cargan solo 8 dígitos, agregamos 53
  if (n.length === 8) {
    n = `53${n}`;
  }

  return n;
};

const datoWhatsApp = (v, fallback = "-") => {
  const t = String(v ?? "").trim();
  return t || fallback;
};

const nombreClienteWhatsApp = (cliente = {}) => {
  return [
    cliente.apellido1,
    cliente.apellido2,
    cliente.nombre,
    cliente.nombreCompleto,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

const nombreReceptorWhatsApp = (receptor = {}) => {
  return [
    receptor.nombre,
    receptor.segundoNombre,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

const productoColorWhatsApp = (producto = {}, item = {}) => {
  const nombre =
    clean(producto.nombreComercial) ||
    clean(producto.modelo) ||
    clean(producto.descripcion) ||
    clean(item.descripcionProducto) ||
    clean(item.modeloProducto) ||
    "-";

  const color =
    clean(item.colorNombre) ||
    clean(producto.colorNombre) ||
    clean(producto.color?.color) ||
    clean(producto.color?.nombre) ||
    clean(producto.color) ||
    "SIN COLOR";

  return `${nombre} / ${color}`;
};

const armarMensajeWhatsAppVenta = (
  venta = {},
  receptorObjetivo = null
) => {
  const cliente =
    venta.clienteCompra || {};

  const acompananteMiami =
    venta.acompananteMiami || {};

  const items =
    Array.isArray(venta.items)
      ? venta.items
      : [];

  const itemsDelReceptor =
    receptorObjetivo
      ? items.filter((item) => {
          const rec =
            item.clienteRecibe || {};

          const idRec =
            getId(rec);

          const idObjetivo =
            getId(receptorObjetivo);

          return (
            idRec &&
            idObjetivo &&
            idRec === idObjetivo
          );
        })
      : items;

  const primerItem =
    itemsDelReceptor[0] ||
    items[0] ||
    {};

  const receptor =
    receptorObjetivo ||
    primerItem.clienteRecibe ||
    {};

  const productosTexto =
    itemsDelReceptor.length > 0
      ? itemsDelReceptor
          .map((item) => {
            const cantidad =
              Number(
                item.cantidad || 1
              );

            return `${cantidad} x ${productoColorWhatsApp(
              item.producto,
              item
            )}`;
          })
          .join(" | ")
      : "-";

  const nombreAcompanante =
    nombreClienteWhatsApp(
      acompananteMiami
    );

  return `
Hola, te enviamos los datos registrados para la recepción del pedido Riders Miami:

Venta N°: ${datoWhatsApp(venta.nroventa)}

Nombre del cliente: ${datoWhatsApp(nombreClienteWhatsApp(cliente))}
Celular del cliente: ${datoWhatsApp(
    cliente.telefono1 ||
      cliente.telefono ||
      cliente.telefonoPrincipal
  )}

Acompañante / garante Miami: ${datoWhatsApp(nombreAcompanante)}

Producto y color: ${datoWhatsApp(productosTexto)}

Nombre de quien recibe: ${datoWhatsApp(nombreReceptorWhatsApp(receptor))}
Apellido 1: ${datoWhatsApp(receptor.apellido1)}
Apellido 2: ${datoWhatsApp(receptor.apellido2)}
Número de carnet: ${datoWhatsApp(receptor.carnetIdentidad)}
Teléfono: ${datoWhatsApp(
    receptor.telefonoPrincipal ||
      receptor.telefono
  )}
Dirección: ${datoWhatsApp(
    receptor.direccionCompleta ||
      receptor.direccion
  )}
Municipio: ${datoWhatsApp(receptor.municipio)}
Provincia: ${datoWhatsApp(receptor.provincia)}

Por favor confirmar que los datos sean correctos.
`.trim();
};

const TELEFONO_WHATSAPP_EMPRESA = "543815176145";

const abrirWhatsAppVenta = (venta = {}, receptorObjetivo = null) => {
  const items = Array.isArray(venta.items) ? venta.items : [];
  const receptor =
    receptorObjetivo || items[0]?.clienteRecibe || venta.clienteRecibe || {};

  const telefonoEmpresa = normalizarTelefonoWhatsApp(TELEFONO_WHATSAPP_EMPRESA);

  if (!telefonoEmpresa) {
    alert("No está configurado el teléfono interno de WhatsApp.");
    return;
  }

  const mensaje = armarMensajeWhatsAppVenta(venta, receptor);
  const url = `https://wa.me/${telefonoEmpresa}?text=${encodeURIComponent(
    mensaje
  )}`;

  window.open(url, "_blank", "noopener,noreferrer");
};

/* ----------------------------- ESTADOS BASE ----------------------------- */

const makeClienteCompraInicial = () => ({
  apellido1: "",
  apellido2: "",
  nombre: "",
  dni: "",
  fecing: fechaHoyArgentina(),
  telefono1: "+1",
  email: "",

  domicilio: "",
  direccion2: "",
  ciudad: "",
  estado: "FL",
  zipCode: "",
  pais: "USA",

  activo: true,
});

const makeReceptorInicial = () => ({
  tempId: makeTempId("rec"),
  codrecep: "",

  nombre: "",
  segundoNombre: "",
  apellido1: "",
  apellido2: "",

  carnetIdentidad: "",

  telefonoPrincipal: "+53",
  email: "",

  direccion: "",
  referenciaDireccion: "",
  provincia: "",
  municipio: "",
  zonaLogistica: "",
  pais: "CUBA",

  activo: true,
});

const makeItemInicial = (receptorKey = "") => ({
  tempId: makeTempId("item"),
  producto: "",
  receptorKey,
  cantidad: 1,
  precioVentaUSD: "",
  costoEnvioUSD: 0,

  coloresPreferidos: [],
  colorEntregado: "",
  observacionColor: "",

  observaciones: "",
});

const ventaInicial = {
  fecha: new Date().toISOString().slice(0, 10),
  observaciones: "",
};

const makePagoInicial = () => ({
  tempId: makeTempId("pago"),
  medioPago: "EFECTIVO EN TIENDA",

  montoUSD: "",
  fondeoFinancieraUSD: "",

  // NUEVO - cálculo bidireccional para GROSS_UP
  totalCobradoManualUSD: "",
  origenCalculoFinanciera: "MONTO_APLICADO",

  fechaPago: new Date().toISOString().slice(0, 10),
  financiera: "",
  clienteFinanciacion: "",
  financiacionModo: "COMPRADOR",

  cuotasFinanciacion: "",

  estadoAcreditacion: "ACREDITADO",
  fechaAcreditacion: new Date().toISOString().slice(0, 10),
  observaciones: "",
});

/* ----------------------------- COMPONENTE ----------------------------- */

const AddVentaRapidaForm = () => {
  const [clienteCompra, setClienteCompra] = useState(() =>
    makeClienteCompraInicial()
  );
  const [receptores, setReceptores] = useState(() => {
    const receptorInicial = makeReceptorInicial();
    return [receptorInicial];
  });

  const [items, setItems] = useState(() => [makeItemInicial()]);
  const [venta, setVenta] = useState(ventaInicial);

  const [colaboradorVenta, setColaboradorVenta] = useState("");
  const [colaboradores, setColaboradores] = useState([]);
  const [qColaborador, setQColaborador] = useState("");
  const [loadingColaboradores, setLoadingColaboradores] =
    useState(false);

  const [
    telefonoClienteCopiado,
    setTelefonoClienteCopiado,
  ] = useState(false);

  const [estadoRecepcion, setEstadoRecepcion] = useState("COMPLETA");

  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [qProducto, setQProducto] = useState("");

  const [qProductoItem, setQProductoItem] = useState({});
  const [productoPickerActivo, setProductoPickerActivo] = useState("");

  const [saving, setSaving] = useState(false);
  const [pasoActivo, setPasoActivo] = useState(1);

  const contenidoPasoRef = useRef(null);

  const irAPasoVentaRapida = (numeroPaso) => {
    const paso = Math.min(
      Math.max(Number(numeroPaso) || 1, 1),
      4
    );

    setPasoActivo(paso);

    // Solo hacemos desplazamiento automático en celular/tablet chica.
    if (
      typeof window === "undefined" ||
      !window.matchMedia("(max-width: 767.98px)").matches
    ) {
      return;
    }

    window.requestAnimationFrame(() => {
      const elemento = contenidoPasoRef.current;

      if (!elemento) return;

      // Dejamos espacio para el navbar superior.
      const offset = 80;

      const top =
        elemento.getBoundingClientRect().top +
        window.scrollY -
        offset;

      window.scrollTo({
        top,
        behavior: "smooth",
      });
    });
  };

  const [ventaWhatsappPendiente, setVentaWhatsappPendiente] = useState(null);
  const [modalVentaOk, setModalVentaOk] = useState({
    show: false,
    nro: "",
    warning: "",
  });

  const [pagosIniciales, setPagosIniciales] = useState(() => [makePagoInicial(),]);
  const [showCalculadoraPaso4, setShowCalculadoraPaso4] = useState(false);
  const [financieras, setFinancieras] = useState([]);
  const [clientesFinanciacion, setClientesFinanciacion] = useState([]);
  const [qClienteFinanciacion, setQClienteFinanciacion] = useState("");

  const [modalGaranteFinanciacion, setModalGaranteFinanciacion] = useState({
    show: false,
    pagoIdx: null,
  });

  const [ocrFrenteFile, setOcrFrenteFile] = useState(null);
  const [ocrFrentePreview, setOcrFrentePreview] = useState("");
  const [leyendoOcrFrente, setLeyendoOcrFrente] = useState(false);
  const [ocrFrenteDatos, setOcrFrenteDatos] = useState(null);

  const [showCropFrente, setShowCropFrente] = useState(false);
  const [ocrFrenteOriginalFile, setOcrFrenteOriginalFile] = useState(null);

  const [ocrDorsoFile, setOcrDorsoFile] = useState(null);
  const [ocrDorsoPreview, setOcrDorsoPreview] = useState("");
  const [leyendoOcrDorso, setLeyendoOcrDorso] = useState(false);
  const [ocrDorsoDatos, setOcrDorsoDatos] = useState(null);

  const [showCropDorso, setShowCropDorso] = useState(false);
  const [ocrDorsoOriginalFile, setOcrDorsoOriginalFile] = useState(null);

  const [ocrReceptores, setOcrReceptores] = useState({});

  const [cropReceptor, setCropReceptor] = useState({
    show: false,
    idx: null,
    tipo: "frente",
    file: null,
  });

const handlePagoInicial = (idx, e) => {
  const { name, value } = e.target;

  setPagosIniciales((prev) =>
    prev.map((pago, i) => {

      let valorProcesado = value;

      if (
        name === "montoUSD" &&
        esFinanciamientoCasa(
          pago.medioPago
        )
      ) {
        const montoIngresado =
          toNumber(value, 0);

        if (
          montoIngresado >
          LIMITE_FINANCIAMIENTO_CASA_USD
        ) {
          valorProcesado = String(
            LIMITE_FINANCIAMIENTO_CASA_USD
          );
        }
      }

      if (i !== idx) return pago;

      const next = {
        ...pago,
        [name]:
          name === "observaciones"
            ? upperTyping(value)
            : valorProcesado,
      };

      /*
        ======================================================
        GROSS UP BIDIRECCIONAL
        ======================================================

        MONTO APLICADO -> TOTAL COBRADO
        sigue funcionando como siempre.

        TOTAL COBRADO -> MONTO APLICADO
        se habilita como alternativa.
      */
      if (name === "montoUSD") {
        next.origenCalculoFinanciera =
          "MONTO_APLICADO";

        next.totalCobradoManualUSD =
          "";
      }

      if (name === "totalCobradoManualUSD") {
        const previewActual =
          calcularFeePagoRapido(
            pago,
            financieras
          );

        const tipoCalculo =
          upper(
            previewActual?.tipoCalculo
          );

        const permiteInversoGrossUp =
          !previewActual?.esFondeoInverso &&
          [
            "GROSS_UP",
            "POR_CUOTA",
          ].includes(tipoCalculo);

        if (permiteInversoGrossUp) {
          const totalIngresadoUSD =
            round2(
              Math.max(
                0,
                toNumber(value, 0)
              )
            );

          const feePorcentaje =
            Math.max(
              0,
              toNumber(
                previewActual?.feePorcentaje,
                0
              )
            );

          const factorNeto =
            1 - feePorcentaje / 100;

          /*
            Cuánto estaba aplicando este mismo pago
            antes de modificar TOTAL COBRADO.
          */
          const detalleActual =
            resumenPagosIniciales
              ?.pagosCalculados?.[idx];

          const montoAplicadoActualUSD =
            round2(
              Math.max(
                0,
                toNumber(
                  detalleActual?.montoRealUSD ??
                    previewActual?.montoAplicadoUSD,
                  0
                )
              )
            );

          /*
            Saldo que este pago puede cubrir.

            Si el medio NO lleva Tax usamos
            el saldo especial sin Tax.
          */
          const pagoSinTaxActual =
            esPagoSinTax(
              pago,
              financieras
            );

          const saldoDisponibleUSD =
            pagoSinTaxActual
              ? saldoPendienteSinTaxUSD
              : resumenPagosIniciales
                  .saldoPendienteUSD;

          /*
            Sumamos lo que este mismo pago
            ya estaba aplicando porque el saldo
            actual ya lo descontó.
          */
          const maxAplicableUSD =
            round2(
              Math.max(
                0,
                saldoDisponibleUSD +
                  montoAplicadoActualUSD
              )
            );

          /*
            Convertimos el máximo aplicable
            al máximo TOTAL COBRADO permitido.
          */
          const maxTotalCobradoUSD =
            factorNeto > 0
              ? round2(
                  maxAplicableUSD /
                    factorNeto
                )
              : maxAplicableUSD;

          /*
            Nunca permitimos cobrar más
            de lo necesario para cerrar la venta.
          */
          const totalCobradoUSD =
            round2(
              Math.min(
                totalIngresadoUSD,
                maxTotalCobradoUSD
              )
            );

          const montoAplicadoUSD =
            factorNeto > 0
              ? round2(
                  totalCobradoUSD *
                    factorNeto
                )
              : totalCobradoUSD;

          next.origenCalculoFinanciera =
            "TOTAL_COBRADO";

          next.totalCobradoManualUSD =
            totalIngresadoUSD >
            maxTotalCobradoUSD
              ? String(
                  maxTotalCobradoUSD.toFixed(2)
                )
              : value;

          next.montoUSD =
            totalCobradoUSD > 0
              ? String(montoAplicadoUSD)
              : "";
        }
      }

      if (name === "financiacionModo") {
        next.financiacionModo = value;

        if (value === "COMPRADOR") {
          next.clienteFinanciacion = "";
        }
      }

      if (name === "medioPago") {
        const medioNuevo = upper(value);
        
        next.totalCobradoManualUSD = "";
        next.origenCalculoFinanciera =
          "MONTO_APLICADO";

        /*
          FINANCIERA EXTERNA
        */
        if (
          medioNuevo ===
          "CREDITO FINANCIERA"
        ) {
          /*
            Venta Rápida:
            las financieras quedan acreditadas
            automáticamente al guardar.
          */
          next.estadoAcreditacion =
            "ACREDITADO";

          next.fechaAcreditacion =
            next.fechaPago ||
            fechaHoyArgentina();

          next.financiacionModo =
            next.financiacionModo ||
            "COMPRADOR";
        }

        /*
          FINANCIAMIENTO CON LA CASA
        */
        else if (
          medioNuevo ===
          "FINANCIAMIENTO CON LA CASA"
        ) {
          next.financiera = "";
          next.clienteFinanciacion = "";
          next.financiacionModo =
            "COMPRADOR";
          next.cuotasFinanciacion = "";
          next.fondeoFinancieraUSD = "";

          next.estadoAcreditacion =
            "PENDIENTE";

          next.fechaAcreditacion = "";

          next.montoUSD = "";
        }

        /*
          TARJETA CREDITO / DEBITO
        */
        else if (
          medioNuevo === "CREDITO" ||
          medioNuevo === "DEBITO"
        ) {
          next.financiera = "";
          next.cuotasFinanciacion = "";
          next.fondeoFinancieraUSD = "";

          next.financiacionModo =
            next.financiacionModo ||
            "COMPRADOR";

          if (
            next.financiacionModo ===
            "COMPRADOR"
          ) {
            next.clienteFinanciacion = "";
          }

          next.estadoAcreditacion =
            "ACREDITADO";

          next.fechaAcreditacion =
            next.fechaPago ||
            new Date()
              .toISOString()
              .slice(0, 10);
        }

        /*
          EFECTIVO / ZELLE / OTROS
        */
        else {
          next.financiera = "";
          next.clienteFinanciacion = "";
          next.financiacionModo =
            "COMPRADOR";
          next.cuotasFinanciacion = "";
          next.fondeoFinancieraUSD = "";

          next.estadoAcreditacion =
            "ACREDITADO";

          next.fechaAcreditacion =
            next.fechaPago ||
            new Date()
              .toISOString()
              .slice(0, 10);
        }
      }

      if (name === "financiera") {
        next.cuotasFinanciacion = "";
        next.fondeoFinancieraUSD = "";
        next.totalCobradoManualUSD = "";
        next.origenCalculoFinanciera =
          "MONTO_APLICADO";

        const financieraNueva =
          getFinancieraById(
            value,
            financieras
          );

        const usaFondeo =
          financieraNueva
            ?.configuracionPagosActiva ===
            true &&
          upper(
            financieraNueva?.tipoCalculo
          ) === "FONDEO_INVERSO";

        if (usaFondeo) {
          next.montoUSD = "";
        }
      }

      if (
        name === "fechaPago" &&
        next.estadoAcreditacion ===
          "ACREDITADO"
      ) {
        next.fechaAcreditacion = value;
      }

      return next;
    })
  );
};

  const agregarPagoInicial = () => {
    setPagosIniciales((prev) => [...prev, makePagoInicial()]);
  };

  const quitarPagoInicial = (idx) => {
    setPagosIniciales((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  /* =========================================================
   APLICAR CALCULADORA RIDERS AL PASO 4
========================================================= */

const aplicarCalculadoraPaso4 = ({
  resultado,
}) => {
  const detalles =
    Array.isArray(
      resultado?.pagosCalculados
    )
      ? resultado.pagosCalculados
      : [];

  const hoy =
    fechaHoyArgentina();

  const nuevosPagos =
    detalles
      .filter(
        (detalle) =>
          !detalle?.error &&
          toNumber(
            detalle?.montoCobradoUSD,
            0
          ) > 0
      )
      .map(
        (detalle) => {
          const medio =
            upper(
              detalle?.medioPago
            );

          const esFinanciera =
            medio ===
            "CREDITO FINANCIERA";

          const tipoCalculo =
            upper(
              detalle
                ?.tipoCalculo
            );

          const esFondeoInverso =
            esFinanciera &&
            tipoCalculo ===
              "FONDEO_INVERSO";

          const esGrossUp =
            esFinanciera &&
            [
              "GROSS_UP",
              "POR_CUOTA",
            ].includes(
              tipoCalculo
            );

      const estadoPendiente =
        medio ===
          "FINANCIAMIENTO CON LA CASA";

          const pagoNuevo = {
            ...makePagoInicial(),

            tempId:
              makeTempId(
                "pago"
              ),

            medioPago:
              medio,

            fechaPago:
              hoy,

            financiera:
              esFinanciera
                ? getId(
                    detalle
                      ?.financiera
                  )
                : "",

            cuotasFinanciacion:
              esFinanciera &&
              toNumber(
                detalle?.cuotas,
                0
              ) > 0
                ? String(
                    detalle.cuotas
                  )
                : "",

            /*
              RESPONSABLE.

              La calculadora todavía
              no selecciona garante.

              Lo dejamos en comprador
              para que el vendedor pueda
              cambiarlo luego en Paso 4.
            */
            financiacionModo:
              "COMPRADOR",

            clienteFinanciacion:
              "",

            /*
              ACREDITACIÓN.
            */
            estadoAcreditacion:
              estadoPendiente
                ? "PENDIENTE"
                : "ACREDITADO",

            fechaAcreditacion:
              estadoPendiente
                ? ""
                : hoy,

            /*
              VALORES.

              Cada tipo de cálculo
              entra al Paso 4 de la
              manera que éste ya espera.
            */
            montoUSD:
              esFondeoInverso
                ? ""
                : String(
                    round2(
                      toNumber(
                        detalle
                          ?.montoAplicadoUSD,
                        0
                      )
                    )
                  ),

            fondeoFinancieraUSD:
              esFondeoInverso
                ? String(
                    round2(
                      toNumber(
                        detalle
                          ?.montoCobradoUSD,
                        0
                      )
                    )
                  )
                : "",

            /*
              GROSS UP / POR CUOTA

              El Paso 4 ya soporta
              el cálculo inverso desde
              TOTAL COBRADO.
            */
            origenCalculoFinanciera:
              esGrossUp
                ? "TOTAL_COBRADO"
                : "MONTO_APLICADO",

            totalCobradoManualUSD:
              esGrossUp
                ? String(
                    round2(
                      toNumber(
                        detalle
                          ?.montoCobradoUSD,
                        0
                      )
                    )
                  )
                : "",

            observaciones:
              "",
          };

          return pagoNuevo;
        }
      );

  if (
    nuevosPagos.length ===
    0
  ) {
    alert(
      "La calculadora no tiene pagos válidos para aplicar."
    );

    return;
  }

  /*
    Si todavía no existe ningún pago real,
    reemplazamos el renglón vacío inicial.

    Si ya existe un pago real,
    por ejemplo ZELLE 2000,
    agregamos los nuevos pagos
    sin borrar los anteriores.
  */
  setPagosIniciales(
    (prev) => {
      const pagosExistentes =
        (Array.isArray(prev)
          ? prev
          : []
        ).filter(
          (pago) => {
            return (
              toNumber(
                pago?.montoUSD,
                0
              ) > 0 ||
              toNumber(
                pago
                  ?.fondeoFinancieraUSD,
                0
              ) > 0 ||
              toNumber(
                pago
                  ?.totalCobradoManualUSD,
                0
              ) > 0
            );
          }
        );

      if (
        pagosExistentes.length ===
        0
      ) {
        return nuevosPagos;
      }

      return [
        ...pagosExistentes,
        ...nuevosPagos,
      ];
    }
  );

  setShowCalculadoraPaso4(
    false
  );
};

  const abrirModalGaranteFinanciacion = (pagoIdx) => {
    setModalGaranteFinanciacion({
      show: true,
      pagoIdx,
    });
  };

  const cerrarModalGaranteFinanciacion = () => {
    setModalGaranteFinanciacion({
      show: false,
      pagoIdx: null,
    });
  };

  const onGaranteFinanciacionGuardado = (garanteCreado) => {
    const id = getId(garanteCreado);

    if (!id) {
      alert("El garante se guardó, pero no se pudo obtener el ID.");
      return;
    }

    // Lo agregamos a la lista local para que aparezca inmediatamente en el select.
    setClientesFinanciacion((prev) => {
      const lista = Array.isArray(prev) ? prev : [];
      const existe = lista.some((c) => getId(c) === id);

      if (existe) {
        return lista.map((c) =>
          getId(c) === id ? { ...c, ...garanteCreado } : c
        );
      }

      return [garanteCreado, ...lista];
    });

    // Lo seleccionamos automáticamente en el pago que abrió el modal.
    setPagosIniciales((prev) =>
      prev.map((pago, i) =>
        i === modalGaranteFinanciacion.pagoIdx
          ? {
              ...pago,
              financiacionModo: "GARANTE",
              clienteFinanciacion: id,
            }
          : pago
      )
    );

    setQClienteFinanciacion("");

    cerrarModalGaranteFinanciacion();
  };

  /* ----------------------------- CARGA PRODUCTOS ----------------------------- */

  useEffect(() => {
    let alive = true;

    const cargarProductos = async () => {
      try {
        setLoadingProductos(true);

        const resp = await getProductos({ limit: 10000 });

        if (!alive) return;

        const lista = unwrapProductos(resp)
          .filter((p) => p?.activo ?? true)
          .sort((a, b) =>
            nombreProducto(a).localeCompare(nombreProducto(b), "es", {
              sensitivity: "base",
            })
          );

        setProductos(lista);
      } catch (err) {
        console.error("❌ Error cargando productos venta rápida:", err);
        alert("No se pudieron cargar los productos.");
      } finally {
        if (alive) setLoadingProductos(false);
      }
    };

    cargarProductos();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const cargarDatosPago = async () => {
      try {
        const [respFinancieras, respClientes] = await Promise.all([
          getFinancieras({ page: 1, limit: 300 }),
          getClientes({ page: 1, limit: 1000 }),
        ]);

        if (!alive) return;

        setFinancieras(
          Array.isArray(respFinancieras?.financieras)
            ? respFinancieras.financieras
            : []
        );

        setClientesFinanciacion(
          Array.isArray(respClientes?.clientes)
            ? respClientes.clientes
            : Array.isArray(respClientes)
            ? respClientes
            : []
        );
      } catch (err) {
        console.error("❌ Error cargando datos de pago:", err);
      }
    };

    cargarDatosPago();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const cargarColaboradores = async () => {
      try {
        setLoadingColaboradores(true);

        const resp =
          await getUsuariosColaboradores();

        if (!alive) return;

        if (resp?.ok === false) {
          console.warn(
            "No se pudieron cargar colaboradores:",
            resp?.err?.message
          );

          setColaboradores([]);
          return;
        }

        setColaboradores(
          Array.isArray(resp?.usuarios)
            ? resp.usuarios
            : []
        );
      } catch (err) {
        console.error(
          "❌ Error cargando colaboradores:",
          err
        );

        if (alive) {
          setColaboradores([]);
        }
      } finally {
        if (alive) {
          setLoadingColaboradores(false);
        }
      }
    };

    cargarColaboradores();

    return () => {
      alive = false;
    };
  }, []);
  
  useEffect(() => {
    const primerReceptor = receptores[0]?.tempId || "";

    if (!primerReceptor) return;

    setItems((prev) =>
      prev.map((item) =>
        item.receptorKey ? item : { ...item, receptorKey: primerReceptor }
      )
    );
  }, [receptores]);

  /* ----------------------------- MEMOS ----------------------------- */

  const productosMap = useMemo(() => {
    const map = {};

    productos.forEach((p) => {
      const id = getId(p);
      if (id) map[id] = p;
    });

    return map;
  }, [productos]);

  const productosFiltrados = useMemo(() => {
    const q = clean(qProducto).toLowerCase();

    if (!q) return productos.slice(0, 120);

    return productos
      .filter((p) => {
        const categoria =
          typeof p?.categoria === "object"
            ? p?.categoria?.nombre || p?.categoria?.descripcion
            : "";

        const marca =
          typeof p?.marca === "object"
            ? p?.marca?.nombre || p?.marca?.marca
            : "";

        const texto = [
          p?.codprod,
          p?.nombreComercial,
          p?.modelo,
          p?.descripcion,
          categoria,
          marca,
          p?.tipoMotor,
        ]
          .map(clean)
          .join(" ")
          .toLowerCase();

        return texto.includes(q);
      })
      .slice(0, 120);
  }, [productos, qProducto]);

  const labelProductoVenta = (prod = {}) => {
  if (!prod) return "";

  const partes = [];

  if (prod.codprod) partes.push(String(prod.codprod));
  partes.push(nombreProducto(prod));

  const precio = toNumber(prod.precioVentaUSD, 0);
  if (precio > 0) partes.push(formatUSD(precio));

  return partes.filter(Boolean).join(" - ");
};

const textoBusquedaProducto = (p = {}) => {
  const categoria =
    typeof p?.categoria === "object"
      ? p?.categoria?.nombre || p?.categoria?.descripcion
      : p?.categoria;

  const marca =
    typeof p?.marca === "object"
      ? p?.marca?.nombre || p?.marca?.marca
      : p?.marca;

  return normalizarTexto(
    [
      p?.codprod,
      p?.nombreComercial,
      p?.modelo,
      p?.descripcion,
      categoria,
      marca,
      p?.tipoMotor,
      labelProductoVenta(p),
    ]
      .map(clean)
      .join(" ")
  );
};

const productosParaItem = (item = {}) => {
  const qLocal = clean(qProductoItem[item.tempId]);
  const qGeneral = clean(qProducto);

  const q = normalizarTexto(qLocal || qGeneral);

  // Sin búsqueda: mostrar todos los productos cargados
  if (!q) return productos.slice(0, 100);

  const terms = q.split(/\s+/).filter(Boolean);

  return productos
    .filter((p) => {
      const texto = textoBusquedaProducto(p);
      return terms.every((term) => texto.includes(term));
    })
    .slice(0, 200);
};

const getProductoInputValue = (item = {}) => {
  const q = qProductoItem[item.tempId];

  if (q !== undefined) return q;

  const prod = productosMap[item.producto];

  return prod ? labelProductoVenta(prod) : "";
};

const handleBuscarProductoItem = (itemTempId, value) => {
  setQProductoItem((prev) => ({
    ...prev,
    [itemTempId]: value,
  }));

  setProductoPickerActivo(itemTempId);
};

const seleccionarProductoItem = (idx, item, productoId) => {
  const prod = productosMap[productoId];

  handleItemChange(idx, "producto", productoId);

  setQProductoItem((prev) => ({
    ...prev,
    [item.tempId]: prod ? labelProductoVenta(prod) : "",
  }));

  setProductoPickerActivo("");
};

const limpiarProductoItem = (idx, item) => {
  handleItemChange(idx, "producto", "");

  setQProductoItem((prev) => ({
    ...prev,
    [item.tempId]: "",
  }));

  setProductoPickerActivo(item.tempId);
};

  const receptoresCalculados = useMemo(
    () =>
      receptores.map((receptor) => ({
        ...receptor,
        nombreCompleto: buildNombreCompleto(receptor),
        direccionCompleta: buildDireccionCompleta(receptor),
        zonaLogistica:
          obtenerZonaLogisticaPorProvincia(receptor.provincia) ||
          upper(receptor.zonaLogistica),
        pais: upper(receptor.pais) || "CUBA",
      })),
    [receptores]
  );

const totales = useMemo(() => {
  let totalProductosUSD = 0;
  let totalEnvioUSD = 0;

  items.forEach((item) => {
    const cantidad = toNumber(item.cantidad, 0);
    const precio = toNumber(item.precioVentaUSD, 0);
    const envio = toNumber(item.costoEnvioUSD, 0);

    totalProductosUSD += cantidad * precio;
    totalEnvioUSD += cantidad * envio;
  });

  totalProductosUSD = round2(totalProductosUSD);
  totalEnvioUSD = round2(totalEnvioUSD);

  // Tax calculado directamente sobre el valor de los productos.
  const taxUSD = round2(
    totalProductosUSD * (TAX_PORCENTAJE / 100)
  );

  // Total final que debe pagar el cliente.
  const totalUSD = round2(
    totalProductosUSD +
      totalEnvioUSD +
      taxUSD
  );

  return {
    totalProductosUSD,
    totalEnvioUSD,

    taxPorcentaje: TAX_PORCENTAJE,
    taxUSD,

    totalUSD,
  };
}, [items]);

const resumenPagosIniciales = useMemo(() => {
  let totalPagosUSD = 0;
  let totalFeeClienteUSD = 0;
  let totalCobrarClienteUSD = 0;
  let totalDevolucionTaxUSD = 0;
  let totalTaxCobradoUSD = 0;

  const pagosCalculados =
    calcularPagosConDevolucionTax(
      pagosIniciales,
      totales.taxUSD,
      financieras,
      totales.totalUSD
    );

  pagosCalculados.forEach((detallePago) => {
    const {
      montoRealUSD,
      devolucionTaxUSD,
      taxCobradoUSD,
      feePreview,
    } = detallePago;

    // Se suma únicamente el dinero real pagado por el cliente.
    totalPagosUSD += montoRealUSD;

    totalDevolucionTaxUSD += devolucionTaxUSD;

    totalFeeClienteUSD += round2(
      toNumber(feePreview.feeMontoUSD, 0)
    );

    totalTaxCobradoUSD +=
      round2(
        toNumber(
          taxCobradoUSD,
          0
        )
      );
    /*
      Dinero que realmente paga el cliente:
      - Efectivo: solamente el efectivo entregado.
      - Financiera: importe bruto incluyendo fee.
      - Otros medios: importe ingresado.
    */
    totalCobrarClienteUSD += round2(
      toNumber(
        feePreview.totalCobrarClienteUSD,
        montoRealUSD
      )
    );
  });

  totalPagosUSD = round2(totalPagosUSD);
  totalFeeClienteUSD = round2(totalFeeClienteUSD);
  totalCobrarClienteUSD = round2(totalCobrarClienteUSD);
  totalDevolucionTaxUSD = round2(totalDevolucionTaxUSD);
  totalTaxCobradoUSD = round2(totalTaxCobradoUSD);

/*
  Tax que realmente queda cobrado después de descontar
  los medios de pago que no llevan Tax.
*/
  const taxRealUSD = round2(
    Math.max(
      totales.taxUSD - totalDevolucionTaxUSD,
      0
    )
  );

  /*
    Base sobre la cual el backend debe calcular el Tax.

    Solo queda gravada la parte pagada con medios
    que efectivamente llevan Tax.
  */
  const taxBaseUSD =
    TAX_PORCENTAJE > 0
      ? round2(
          taxRealUSD /
            (TAX_PORCENTAJE / 100)
        )
      : 0;

  const totalRealCobrarUSD = round2(
    totales.totalUSD - totalDevolucionTaxUSD
  );

  const saldoReal = round2(
    totalRealCobrarUSD - totalPagosUSD
  );

  return {
    pagosCalculados,

    totalPagosUSD,
    totalFeeClienteUSD,
    totalCobrarClienteUSD,
    totalDevolucionTaxUSD,
    totalRealCobrarUSD,
    totalTaxCobradoUSD,

    taxRealUSD,
    taxBaseUSD,

    saldoPendienteUSD: saldoReal > 0 ? saldoReal : 0,
    excedenteUSD: saldoReal < 0 ? Math.abs(saldoReal) : 0,
  };
  }, [
    pagosIniciales,
    financieras,
    totales.taxUSD,
    totales.totalUSD,
  ]);

  /*
    Importe sugerido cuando toda la venta se paga
    en EFECTIVO EN TIENDA, sin cobrar Tax.
  */
  const efectivoTotalSugeridoUSD = round2(
    totales.totalProductosUSD + totales.totalEnvioUSD
  );

  const saldoPendienteSinTaxUSD = (() => {
    const saldoActual = round2(
      Math.max(
        resumenPagosIniciales.saldoPendienteUSD,
        0
      )
    );

    const taxDisponible = round2(
      Math.max(
        resumenPagosIniciales.taxRealUSD,
        0
      )
    );

    if (saldoActual <= 0) {
      return 0;
    }

    if (taxDisponible <= 0) {
      return saldoActual;
    }

    const candidatoUSD = round2(
      saldoActual /
        (1 + TAX_PORCENTAJE / 100)
    );

    const taxCandidatoUSD = round2(
      candidatoUSD *
        (TAX_PORCENTAJE / 100)
    );

    if (
      taxCandidatoUSD <=
      taxDisponible + 0.01
    ) {
      return candidatoUSD;
    }

    return round2(
      Math.max(
        saldoActual - taxDisponible,
        0
      )
    );
  })();

  const getFondeoEsperadoVisualUSD = (
  pago = {},
  feePreview = {}
) => {
  if (!feePreview?.esFondeoInverso) {
    return 0;
  }

  /*
    Si el vendedor ya informó un fondeo,
    usamos el cálculo real existente.
  */
  const fondeoInformadoUSD =
    round2(
      Math.max(
        0,
        toNumber(
          feePreview?.fondeoFinancieraUSD,
          0
        )
      )
    );

  if (fondeoInformadoUSD > 0) {
    return round2(
      toNumber(
        feePreview?.fondeoEsperadoUSD,
        0
      )
    );
  }

  /*
    Si todavía NO escribió el fondeo,
    calculamos cuánto debería fondear
    la financiera para completar la venta.
  */
  const pagoSinTax =
    esPagoSinTax(
      pago,
      financieras
    );

  const montoObjetivoUSD =
    pagoSinTax
      ? saldoPendienteSinTaxUSD
      : resumenPagosIniciales
          .saldoPendienteUSD;

  const feePorcentaje =
    Math.max(
      0,
      toNumber(
        feePreview?.feePorcentaje,
        0
      )
    );

  const factorNeto =
    1 - feePorcentaje / 100;

  if (factorNeto <= 0) {
    return round2(
      montoObjetivoUSD
    );
  }

  return round2(
    montoObjetivoUSD /
      factorNeto
  );
};

  const clientesFinanciacionFiltrados = useMemo(() => {
    const q = clean(qClienteFinanciacion).toUpperCase();

    const lista = Array.isArray(clientesFinanciacion)
      ? clientesFinanciacion
      : [];

    if (!q) return lista.slice(0, 80);

    return lista
      .filter((c) => {
        const texto = [
          c.codcli,
          c.razonsocial,
          c.nombre,
          c.apellido1,
          c.apellido2,
          c.telefono,
          c.telefono1,
          c.telefonoPrincipal,
          c.email,
          c.dni,
        ]
          .map(clean)
          .join(" ")
          .toUpperCase();

        return texto.includes(q);
      })
      .slice(0, 80);
  }, [clientesFinanciacion, qClienteFinanciacion]);


  const colaboradoresFiltrados = useMemo(() => {
  const q = clean(qColaborador)
    .toUpperCase();

  const lista = Array.isArray(colaboradores)
    ? colaboradores
    : [];

  if (!q) {
    return lista;
  }

  return lista.filter((usuario) => {
    const texto = [
      usuario.nombres,
      usuario.apellidos,
      usuario.email,
      usuario.sede,
    ]
      .map(clean)
      .join(" ")
      .toUpperCase();

    return texto.includes(q);
  });
}, [colaboradores, qColaborador]);


const copiarTelefonoCliente = async () => {
  const digits =
    getTelefonoUSADigitos(
      clienteCompra.telefono1
    );

  if (digits.length !== 10) {
    alert(
      "Completá los 10 dígitos del teléfono antes de copiar."
    );

    return;
  }

  const telefono =
    telefonoUSAContinuo(
      clienteCompra.telefono1
    );

  try {
    await copiarAlPortapapeles(
      telefono
    );

    setTelefonoClienteCopiado(
      true
    );

    window.setTimeout(() => {
      setTelefonoClienteCopiado(
        false
      );
    }, 1500);
  } catch (err) {
    console.error(
      "Error copiando teléfono:",
      err
    );

    alert(
      "No se pudo copiar el teléfono."
    );
  }
};

  /* ----------------------------- HANDLERS ----------------------------- */

  const handleClienteCompra = (e) => {
    const { name, value } = e.target;

    /*
      TELÉFONO USA

      En el estado lo mantenemos limpio:
      +17863653986

      La máscara se aplica solamente
      al mostrarlo en pantalla.
    */
    if (name === "telefono1") {
      setClienteCompra((prev) => ({
        ...prev,
        telefono1:
          normalizarTelefonoUSA(value),
      }));

      return;
    }

    const camposConEspacios = [
      "apellido1",
      "apellido2",
      "nombre",
      "domicilio",
      "direccion2",
      "ciudad",
      "estado",
      "zipCode",
      "pais",
    ];

    setClienteCompra((prev) => ({
      ...prev,

      [name]:
        name === "email"
          ? lower(value)
          : camposConEspacios.includes(
              name
            )
          ? upperTyping(value)
          : upper(value),
    }));
  };

  const handleReceptorChange = (idx, name, value) => {
    setReceptores((prev) =>
      prev.map((receptor, i) => {
        if (i !== idx) return receptor;

        // Solo cliente receptor Cuba:
        // Carnet siempre 11 dígitos.
        if (name === "carnetIdentidad") {
          return {
            ...receptor,
            carnetIdentidad: limitDigits(value, 11),
          };
        }

        // Teléfono Cuba: + seguido de 10 dígitos.
        if (name === "telefonoPrincipal") {
          return {
            ...receptor,
            telefonoPrincipal: normalizarTelefonoCuba(value),
          };
        }

        const camposConEspacios = [
          "nombre",
          "segundoNombre",
          "apellido1",
          "apellido2",
          "direccion",
          "referenciaDireccion",
          "municipio",
          "provincia",
          "pais",
        ];

        const next = {
          ...receptor,

          [name]:
            name === "email"
              ? lower(value)
              : camposConEspacios.includes(name)
              ? upperTyping(value)
              : upper(value),
        };

        if (name === "provincia") {
          next.provincia = value;
          next.zonaLogistica = obtenerZonaLogisticaPorProvincia(value);
        }

        if (name === "pais") {
          next.pais = upper(value) || "CUBA";
        }

        return next;
      })
    );
  };

  const handleVenta = (e) => {
    const { name, value } = e.target;

    setVenta((prev) => ({
      ...prev,
      [name]: name === "observaciones" ? upperTyping(value) : value,
    }));
  };

  const handleItemChange = (idx, name, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;

        if (name === "producto") {
          const prod = productosMap[value] || {};
          const precio = value ? getProductoPrecio(prod) : "";

          return {
            ...item,
            producto: value,
            precioVentaUSD: precio,

            // En Venta Rápida no se suma envío automáticamente.
            costoEnvioUSD: 0,

            // Al cambiar de producto se limpian los colores anteriores.
            coloresPreferidos: [],
            colorEntregado: "",
            observacionColor: "",
          };
        }

        return {
          ...item,
          [name]: name === "observaciones" ? upperTyping(value) : value,
        };
      })
    );
  };

  const toggleColorPreferido = (idx, colorId) => {
    if (!colorId) return;

    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;

        const actuales = Array.isArray(item.coloresPreferidos)
          ? item.coloresPreferidos
          : [];

        const seleccionado = actuales.includes(colorId);

        return {
          ...item,

          coloresPreferidos: seleccionado
            ? actuales.filter((id) => id !== colorId)
            : [...actuales, colorId],
        };
      })
    );
  };

  const agregarReceptor = () => {
    setReceptores((prev) => [...prev, makeReceptorInicial()]);
  };

  const quitarReceptor = (idx) => {
    if (receptores.length <= 1) return;

    const receptorEliminado = receptores[idx];
    const restantes = receptores.filter((_, i) => i !== idx);
    const primerDisponible = restantes[0]?.tempId || "";

    setReceptores(restantes);

    setItems((itemsPrev) =>
      itemsPrev.map((item) =>
        item.receptorKey === receptorEliminado.tempId
          ? { ...item, receptorKey: primerDisponible }
          : item
      )
    );
  };

  const agregarItem = () => {
    const primerReceptor = receptores[0]?.tempId || "";
    setItems((prev) => [...prev, makeItemInicial(primerReceptor)]);
  };

  const quitarItem = (idx) => {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const limpiarFormulario = () => {
    const receptorInicial = makeReceptorInicial();

    setClienteCompra(makeClienteCompraInicial());
    setReceptores([receptorInicial]);
    setItems([makeItemInicial(receptorInicial.tempId)]);
    setVenta(ventaInicial);

    setPasoActivo(1);
    setQProducto("");
    setEstadoRecepcion("COMPLETA");
    setQProductoItem({});
    setProductoPickerActivo("");

    setPagosIniciales([makePagoInicial()]);
    setQClienteFinanciacion("");
    setShowCalculadoraPaso4(false);

    setColaboradorVenta("");
    setQColaborador("");
    setTelefonoClienteCopiado(false);

    setModalGaranteFinanciacion({
      show: false,
      pagoIdx: null,
    });

    limpiarFrenteDocumento();
    limpiarDorsoDocumento();

    setOcrReceptores({});
    setCropReceptor({
      show: false,
      idx: null,
      tipo: "frente",
      file: null,
    });
    setModalVentaOk({
      show: false,
      nro: "",
      warning: "",
    });
    setVentaWhatsappPendiente(null);
  };

  const cerrarModalVentaOk = () => {
    setModalVentaOk({
      show: false,
      nro: "",
      warning: "",
    });

    limpiarFormulario();
  };

  /* ----------------------------- VALIDACIONES ----------------------------- */

  const validarReceptor = (receptor, idx) => {
    const nro = idx + 1;

    const carnetDigits = onlyDigits(receptor.carnetIdentidad);

    if (!carnetDigits) {
      return `Completá Carnet de identidad del receptor ${nro}.`;
    }

    if (carnetDigits.length !== 11) {
      return `El Carnet de identidad del receptor ${nro} debe tener 11 dígitos.`;
    }

    if (!upper(receptor.nombre)) {
      return `Completá Nombre del receptor ${nro}.`;
    }

    if (!upper(receptor.apellido1)) {
      return `Completá Apellido 1 del receptor ${nro}.`;
    }

    const telefono = clean(receptor.telefonoPrincipal);

    if (!telefono) {
      return `Completá Teléfono principal del receptor ${nro}.`;
    }

    if (!esTelefonoCubaValido(telefono)) {
      return `El Teléfono principal del receptor ${nro} debe tener formato + seguido de 10 dígitos. Ej: +5355531234`;
    }

    if (!upper(receptor.direccion)) {
      return `Completá Dirección del receptor ${nro}.`;
    }

    if (!upper(receptor.provincia)) {
      return `Completá Provincia del receptor ${nro}.`;
    }

    if (!upper(receptor.municipio)) {
      return `Completá Municipio del receptor ${nro}.`;
    }

    return "";
  };

  const validar = () => {
    if (!upper(clienteCompra.apellido1)) {
      return "Completá Apellido 1 del comprador.";
    }

    if (!upper(clienteCompra.nombre)) {
      return "Completá Nombre del comprador.";
    }

    if (!upper(clienteCompra.dni)) {
      return "Completá ID / Documento del comprador.";
    }

    const telefonoClienteDigits =
      getTelefonoUSADigitos(
        clienteCompra.telefono1
      );

    if (
      telefonoClienteDigits.length > 0 &&
      telefonoClienteDigits.length !== 10
    ) {
      return (
        "El teléfono del comprador debe tener " +
        "10 dígitos. Ejemplo: +1 (786)-365-3986."
      );
    }

    if (
      estadoRecepcion === "COMPLETA" &&
      (!Array.isArray(receptores) || receptores.length === 0)
    ) {
      return "La venta debe tener al menos un receptor.";
    }

    if (!Array.isArray(items) || items.length === 0) {
      return "La venta debe tener al menos un producto.";
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const nro = i + 1;

      if (!upper(item.producto)) {
        return `Seleccioná producto del ítem ${nro}.`;
      }

      if (
        estadoRecepcion === "COMPLETA" &&
        !upper(item.receptorKey)
      ) {
        return `Seleccioná receptor del ítem ${nro}.`;
      }

      if (toNumber(item.cantidad, 0) <= 0) {
        return `La cantidad del ítem ${nro} debe ser mayor a cero.`;
      }

      if (toNumber(item.precioVentaUSD, 0) <= 0) {
        return `El precio del ítem ${nro} debe ser mayor a cero.`;
      }
    }

    if (estadoRecepcion === "COMPLETA") {
      const receptoresUsados = new Set(
        items
          .map((item) => item.receptorKey)
          .filter(Boolean)
      );

      for (
        let i = 0;
        i < receptoresCalculados.length;
        i++
      ) {
        const receptor = receptoresCalculados[i];

        if (!receptoresUsados.has(receptor.tempId)) {
          continue;
        }

        const msg = validarReceptor(receptor, i);

        if (msg) return msg;
      }
    }

    const totalFinanciamientoCasaUSD =
      round2(
        pagosIniciales.reduce(
          (total, pago) => {
            if (
              !esFinanciamientoCasa(
                pago.medioPago
              )
            ) {
              return total;
            }

            return (
              total +
              toNumber(
                pago.montoUSD,
                0
              )
            );
          },
          0
        )
      );

    if (
      totalFinanciamientoCasaUSD >
      LIMITE_FINANCIAMIENTO_CASA_USD
    ) {
      return (
        "FINANCIAMIENTO CON LA CASA permite financiar " +
        `como máximo ${formatUSD(
          LIMITE_FINANCIAMIENTO_CASA_USD
        )}. ` +
        `Actualmente se ingresaron ${formatUSD(
          totalFinanciamientoCasaUSD
        )}.`
      );
    }

    if (
      resumenPagosIniciales.totalPagosUSD >
      resumenPagosIniciales.totalRealCobrarUSD
    ) {
      return `El monto aplicado a la venta no puede superar el total real de la venta: ${formatUSD(
        resumenPagosIniciales.totalRealCobrarUSD
      )}.

    Recordá: el fee financiero no se carga en este campo. El sistema lo calcula aparte.`;
    }

    for (let i = 0; i < pagosIniciales.length; i++) {
      const pago =
        pagosIniciales[i];

      const nro =
        i + 1;

      const detallePago =
        resumenPagosIniciales
          .pagosCalculados[i];

      const feePreview =
        detallePago?.feePreview ||
        calcularFeePagoRapido(
          pago,
          financieras
        );

      /*
        FONDEO INVERSO:
        el vendedor debe ingresar exactamente
        el fondeo esperado, incluyendo centavos.

        No permitimos guardar diferencias aunque
        sean menores a USD 1.
      */
      if (feePreview?.esFondeoInverso) {
        const fondeoInformadoUSD =
          round2(
            Math.max(
              0,
              toNumber(
                feePreview?.fondeoFinancieraUSD,
                0
              )
            )
          );

        if (fondeoInformadoUSD > 0) {
          const fondeoEsperadoUSD =
            round2(
              getFondeoEsperadoVisualUSD(
                pago,
                feePreview
              )
            );

          const informadoCentavos =
            Math.round(
              fondeoInformadoUSD * 100
            );

          const esperadoCentavos =
            Math.round(
              fondeoEsperadoUSD * 100
            );

          if (
            informadoCentavos !==
            esperadoCentavos
          ) {
            const diferenciaUSD =
              round2(
                fondeoEsperadoUSD -
                  fondeoInformadoUSD
              );

            if (diferenciaUSD > 0) {
              return (
                `El fondeo del pago ${nro} no está completo.\n\n` +
                `Fondeo informado: ${formatUSD(
                  fondeoInformadoUSD
                )}\n` +
                `Fondeo correcto: ${formatUSD(
                  fondeoEsperadoUSD
                )}\n` +
                `Falta fondear: ${formatUSD(
                  diferenciaUSD
                )}`
              );
            }

            return (
              `El fondeo del pago ${nro} supera el importe correcto.\n\n` +
              `Fondeo informado: ${formatUSD(
                fondeoInformadoUSD
              )}\n` +
              `Fondeo correcto: ${formatUSD(
                fondeoEsperadoUSD
              )}\n` +
              `Exceso: ${formatUSD(
                Math.abs(diferenciaUSD)
              )}`
            );
          }
        }
      }

      const monto =
        round2(
          toNumber(
            detallePago?.montoRealUSD ??
              feePreview.montoAplicadoUSD,
            0
          )
        );

      if (monto <= 0) {
        continue;
      }

      const medioPago =
        upper(pago.medioPago);

      const modoFinanciacion =
        pago.financiacionModo ||
        "COMPRADOR";

      if (!medioPago) {
        return `Seleccioná medio de pago del pago ${nro}.`;
      }

      if (
        medioPago ===
          "CREDITO FINANCIERA" &&
        !getId(pago.financiera)
      ) {
        return `Seleccioná financiera del pago ${nro}.`;
      }

      if (
        medioPago ===
          "CREDITO FINANCIERA" &&
        feePreview.requiereCuotas
      ) {
        const cuotasElegidas =
          toNumber(
            pago.cuotasFinanciacion,
            0
          );

        const cuotasValidas =
          Array.isArray(
            feePreview.cuotasDisponibles
          )
            ? feePreview.cuotasDisponibles
            : [];

        if (
          cuotasElegidas <= 0 ||
          (
            cuotasValidas.length > 0 &&
            !cuotasValidas.includes(
              cuotasElegidas
            )
          )
        ) {
          return `Seleccioná una cantidad válida de cuotas para el pago ${nro}.`;
        }
      }

      if (
        permiteResponsablePago(
          medioPago
        ) &&
        modoFinanciacion ===
          "GARANTE" &&
        !getId(
          pago.clienteFinanciacion
        )
      ) {
        return `Seleccioná o creá el acompañante / garante del pago ${nro}.`;
      }
    }

    return "";
  };

  /* ----------------------------- BUSCAR / CREAR ----------------------------- */

  const obtenerOCrearClienteCompra = async (clientePayload) => {
    const dni = upper(clientePayload.dni);

    if (!dni) {
      throw new Error("Falta DNI / ID del comprador.");
    }

    const encontrados = await buscarClientes({
      q: dni,
      limit: 10,
    });

    const existente = encontrados.find(
      (c) =>
        upper(c?.dni) === dni ||
        upper(c?.documento) === dni ||
        upper(c?.idDocumento) === dni
    );

    if (existente?._id) {
      console.log("✅ Cliente comprador existente:", existente);
      return existente;
    }

    const resp = await postCliente(clientePayload);
    const creado = unwrapClienteGuardado(resp);

    if (!creado?._id) {
      throw new Error("No se pudo obtener el ID del cliente comprador creado.");
    }

    console.log("✅ Cliente comprador creado:", creado);
    return creado;
  };

  const obtenerOCrearClienteRecepcion = async (receptorPayload) => {
    const carnet = upper(receptorPayload.carnetIdentidad);

    if (!carnet) {
      throw new Error("Falta Carnet de identidad del receptor.");
    }

    const encontrados = await buscarClientesRecepcion({
      q: carnet,
      limit: 10,
    });

    const existente = encontrados.find(
      (c) =>
        upper(c?.carnetIdentidad) === carnet ||
        upper(c?.ci) === carnet ||
        upper(c?.documento) === carnet ||
        upper(c?.idDocumento) === carnet
    );

    if (existente?._id) {
      console.log("✅ Receptor existente:", existente);
      return existente;
    }

    const resp = await postClienteRecepcion(receptorPayload);
    const creado = unwrapReceptorGuardado(resp);

    if (!creado?._id) {
      throw new Error("No se pudo obtener el ID del receptor creado.");
    }

    console.log("✅ Receptor creado:", creado);
    return creado;
  };


  const seleccionarFrenteDocumento = (e) => {
    const file = e.target.files?.[0] || null;

    if (!file) return;

    setOcrFrenteOriginalFile(file);
    setOcrFrenteDatos(null);
    setShowCropFrente(true);

    e.target.value = "";
  };

  const leerFrenteDocumentoConIA = async (fileParam = null) => {
    const fileALeer = fileParam || ocrFrenteFile;

    if (!fileALeer) {
      alert("Primero seleccioná una imagen del frente del documento.");
      return;
    }

    try {
      setLeyendoOcrFrente(true);

      const resp = await leerDocumentoClienteOCR({
        file: fileALeer,
        tipoDocumento: "frente",
        destino: "clienteCompra",
      });

      const datos = resp?.datos || null;

      if (!datos) {
        alert("La IA no devolvió datos del documento.");
        return;
      }

      setOcrFrenteDatos(datos);

      const patch = mapOcrAClienteCompra(datos);

      setClienteCompra((prev) => ({
        ...prev,
        ...patch,
      }));

      console.log("✅ OCR frente comprador:", datos);
      console.log("✅ Patch aplicado al comprador:", patch);

      // alert("Documento leído. Revisá los datos antes de guardar.");
    } catch (err) {
      console.error("❌ Error OCR frente:", err?.response?.data || err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo leer el documento."
      );
    } finally {
      setLeyendoOcrFrente(false);
    }
  };

  const guardarFrenteRecortado = async ({ file, previewUrl }) => {
    setShowCropFrente(false);
    setOcrFrenteOriginalFile(null);

    setOcrFrenteFile(file);
    setOcrFrenteDatos(null);

    if (ocrFrentePreview) {
      URL.revokeObjectURL(ocrFrentePreview);
    }

    setOcrFrentePreview(previewUrl || "");

    await leerFrenteDocumentoConIA(file);
  };

  const cancelarCropFrente = () => {
    setShowCropFrente(false);
    setOcrFrenteOriginalFile(null);
  };

  const limpiarFrenteDocumento = () => {
    setOcrFrenteFile(null);
    setOcrFrenteDatos(null);
    setOcrFrenteOriginalFile(null);
    setShowCropFrente(false);

    if (ocrFrentePreview) {
      URL.revokeObjectURL(ocrFrentePreview);
    }

    setOcrFrentePreview("");
  };

  const seleccionarDorsoDocumento = (e) => {
  const file = e.target.files?.[0] || null;

  if (!file) return;

  setOcrDorsoOriginalFile(file);
  setOcrDorsoDatos(null);
  setShowCropDorso(true);

  e.target.value = "";
};

  const mapOcrDorsoAClienteCompraDireccion = (datos = {}) => {
    const comprador =
      datos?.comprador ||
      datos?.clienteCompra ||
      datos?.persona ||
      {};

    const direccion =
      datos?.direccion ||
      comprador?.direccion ||
      {};

    const patch = {};

    const domicilio =
      direccion.domicilio ||
      direccion.direccion ||
      direccion.addressLine1 ||
      direccion.address ||
      comprador.domicilio ||
      comprador.direccion ||
      datos.domicilio ||
      "";

    const direccion2 =
      direccion.direccion2 ||
      direccion.apartamento ||
      direccion.apto ||
      direccion.suite ||
      direccion.unidad ||
      "";

    const ciudad =
      direccion.ciudad ||
      direccion.localidad ||
      direccion.municipio ||
      "";

    const estado =
      direccion.estado ||
      direccion.provincia ||
      "";

    const zipCode =
      direccion.zipCode ||
      direccion.codigoPostal ||
      direccion.postalCode ||
      direccion.zip ||
      "";

    const pais =
      direccion.pais ||
      direccion.country ||
      "";

    if (domicilio) patch.domicilio = upper(domicilio);
    if (direccion2) patch.direccion2 = upper(direccion2);
    if (ciudad) patch.ciudad = upper(ciudad);
    if (estado) patch.estado = upper(estado);
    if (zipCode) patch.zipCode = upper(zipCode);
    if (pais) patch.pais = upper(pais);

    return patch;
  };

  const leerDorsoDocumentoConIA = async (fileParam = null) => {
    const fileALeer = fileParam || ocrDorsoFile;

    if (!fileALeer) {
      alert("Primero seleccioná una imagen del dorso del documento.");
      return;
    }

    try {
      setLeyendoOcrDorso(true);

      const resp = await leerDocumentoClienteOCR({
        file: fileALeer,
        tipoDocumento: "dorso",
        destino: "clienteCompra",
      });

      const datos = resp?.datos || null;

      if (!datos) {
        alert("La IA no devolvió datos del dorso.");
        return;
      }

      setOcrDorsoDatos(datos);

      const patch = mapOcrDorsoAClienteCompraDireccion(datos);

      setClienteCompra((prev) => ({
        ...prev,

        // El dorso SOLO puede completar dirección.
        // No toca nombre, apellido ni documento.
        domicilio: patch.domicilio || prev.domicilio,
        direccion2: patch.direccion2 || prev.direccion2,
        ciudad: patch.ciudad || prev.ciudad,
        estado: patch.estado || prev.estado,
        zipCode: patch.zipCode || prev.zipCode,
        pais: patch.pais || prev.pais,
      }));

      console.log("✅ OCR dorso comprador:", datos);
      console.log("✅ Patch SOLO domicilio aplicado:", patch);

      // alert("Dorso leído. Revisá el domicilio antes de guardar.");
    } catch (err) {
      console.error("❌ Error OCR dorso:", err?.response?.data || err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo leer el dorso del documento."
      );
    } finally {
      setLeyendoOcrDorso(false);
    }
  };

  const guardarDorsoRecortado = async ({ file, previewUrl }) => {
    setShowCropDorso(false);
    setOcrDorsoOriginalFile(null);

    setOcrDorsoFile(file);
    setOcrDorsoDatos(null);

    if (ocrDorsoPreview) {
      URL.revokeObjectURL(ocrDorsoPreview);
    }

    setOcrDorsoPreview(previewUrl || "");

    await leerDorsoDocumentoConIA(file);
  };

  const cancelarCropDorso = () => {
    setShowCropDorso(false);
    setOcrDorsoOriginalFile(null);
  };

  const limpiarDorsoDocumento = () => {
    setOcrDorsoFile(null);
    setOcrDorsoDatos(null);
    setOcrDorsoOriginalFile(null);
    setShowCropDorso(false);

    if (ocrDorsoPreview) {
      URL.revokeObjectURL(ocrDorsoPreview);
    }

    setOcrDorsoPreview("");
  };

  const getOcrReceptorKey = (receptor, tipo) =>
  `${receptor?.tempId || ""}-${tipo}`;

  const setOcrReceptorParcial = (idx, tipo, patch = {}) => {
    const receptor = receptores[idx];
    if (!receptor) return;

    const key = getOcrReceptorKey(receptor, tipo);

    setOcrReceptores((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...patch,
      },
    }));
  };

  const normalizarProvinciaCubaSelect = (provincia = "") => {
    const p = normalizarTexto(provincia);

    return (
      CUBA_PROVINCIAS.find((prov) => normalizarTexto(prov) === p) ||
      upper(provincia)
    );
  };

  const mapOcrAClienteRecepcionLocal = (datos = {}) => {
    const receptor =
      datos?.receptor ||
      datos?.clienteRecepcion ||
      datos?.comprador ||
      datos?.persona ||
      {};

    const direccion = datos?.direccion || receptor?.direccion || {};

    const patch = {};

    const nombre = receptor.nombre || receptor.firstName;
    const segundoNombre = receptor.segundoNombre || receptor.middleName;
    const apellido1 = receptor.apellido1 || receptor.lastName || receptor.apellido;
    const apellido2 = receptor.apellido2 || receptor.segundoApellido;

    const carnet =
      receptor.carnetIdentidad ||
      receptor.dni ||
      receptor.documento ||
      receptor.idDocumento ||
      receptor.numeroDocumento;

    if (nombre) patch.nombre = upper(nombre);
    if (segundoNombre) patch.segundoNombre = upper(segundoNombre);
    if (apellido1) patch.apellido1 = upper(apellido1);
    if (apellido2) patch.apellido2 = upper(apellido2);
    if (carnet) patch.carnetIdentidad = upper(carnet);

    const dir =
      direccion.direccion ||
      direccion.domicilio ||
      direccion.addressLine1 ||
      direccion.address ||
      receptor.direccion;

    const referencia =
      direccion.referenciaDireccion ||
      direccion.referencia ||
      direccion.entreCalles ||
      direccion.entre_calle ||
      "";

    const provincia = direccion.provincia || direccion.estado || "";
    const municipio = direccion.municipio || direccion.ciudad || "";

    if (dir) patch.direccion = upper(dir);
    if (referencia) patch.referenciaDireccion = upper(referencia);

    if (provincia) {
      patch.provincia = normalizarProvinciaCubaSelect(provincia);
      patch.zonaLogistica = obtenerZonaLogisticaPorProvincia(patch.provincia);
    }

    if (municipio) patch.municipio = upper(municipio);

    patch.pais = "CUBA";

    return patch;
  };

  const mapOcrDorsoAClienteRecepcionDireccion = (datos = {}) => {
    const receptor =
      datos?.receptor ||
      datos?.clienteRecepcion ||
      datos?.comprador ||
      datos?.persona ||
      {};

    const direccion = datos?.direccion || receptor?.direccion || {};

    const patch = {};

    const dir =
      direccion.direccion ||
      direccion.domicilio ||
      direccion.addressLine1 ||
      direccion.address ||
      receptor.direccion ||
      "";

    const referencia =
      direccion.referenciaDireccion ||
      direccion.referencia ||
      direccion.entreCalles ||
      direccion.entre_calle ||
      "";

    const provincia = direccion.provincia || direccion.estado || "";
    const municipio = direccion.municipio || direccion.ciudad || "";

    if (dir) patch.direccion = upper(dir);
    if (referencia) patch.referenciaDireccion = upper(referencia);

    if (provincia) {
      patch.provincia = normalizarProvinciaCubaSelect(provincia);
      patch.zonaLogistica = obtenerZonaLogisticaPorProvincia(patch.provincia);
    }

    if (municipio) patch.municipio = upper(municipio);

    patch.pais = "CUBA";

    return patch;
  };

  const aplicarPatchReceptor = (idx, patch = {}) => {
    setReceptores((prev) =>
      prev.map((receptor, i) => {
        if (i !== idx) return receptor;

        const next = {
          ...receptor,
          ...patch,
        };

        if (patch.provincia) {
          next.zonaLogistica = obtenerZonaLogisticaPorProvincia(patch.provincia);
        }

        return next;
      })
    );
  };

  const seleccionarDocumentoReceptor = (idx, tipo, e) => {
    const file = e.target.files?.[0] || null;

    if (!file) return;

    setCropReceptor({
      show: true,
      idx,
      tipo,
      file,
    });

    e.target.value = "";
  };

  const cancelarCropReceptor = () => {
    setCropReceptor({
      show: false,
      idx: null,
      tipo: "frente",
      file: null,
    });
  };

  const leerDocumentoReceptorConIA = async (
    idx,
    tipo = "frente",
    fileParam = null
  ) => {
    const receptor = receptores[idx];

    if (!receptor) {
      alert("No se encontró el receptor.");
      return;
    }

    const key = getOcrReceptorKey(receptor, tipo);
    const dataActual = ocrReceptores[key] || {};
    const fileALeer = fileParam || dataActual.file;

    if (!fileALeer) {
      alert("Primero seleccioná una imagen del documento del receptor.");
      return;
    }

    try {
      setOcrReceptorParcial(idx, tipo, { leyendo: true });

      const resp = await leerDocumentoClienteOCR({
        file: fileALeer,
        tipoDocumento: tipo,
        destino: "clienteRecepcion",
      });

      const datos = resp?.datos || null;

      if (!datos) {
        alert("La IA no devolvió datos del documento del receptor.");
        return;
      }

      const patch =
        tipo === "dorso"
          ? mapOcrDorsoAClienteRecepcionDireccion(datos)
          : mapOcrAClienteRecepcionLocal(datos);

      setOcrReceptorParcial(idx, tipo, {
        datos,
        leyendo: false,
      });

      aplicarPatchReceptor(idx, patch);

      console.log("✅ OCR receptor:", tipo, datos);
      console.log("✅ Patch receptor aplicado:", patch);

      // alert(
      //   tipo === "frente"
      //     ? "Frente del receptor leído. Revisá nombre, apellido y carnet."
      //     : "Dorso del receptor leído. Revisá dirección, provincia y municipio."
      // );
    } catch (err) {
      console.error("❌ Error OCR receptor:", err?.response?.data || err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo leer el documento del receptor."
      );
    } finally {
      setOcrReceptorParcial(idx, tipo, { leyendo: false });
    }
  };

  const guardarReceptorRecortado = async ({ file, previewUrl }) => {
    const { idx, tipo } = cropReceptor;

    if (idx === null || idx === undefined) return;

    const receptor = receptores[idx];
    const key = getOcrReceptorKey(receptor, tipo);
    const previewAnterior = ocrReceptores[key]?.previewUrl;

    if (previewAnterior) {
      URL.revokeObjectURL(previewAnterior);
    }

    setOcrReceptorParcial(idx, tipo, {
      file,
      previewUrl,
      datos: null,
    });

    setCropReceptor({
      show: false,
      idx: null,
      tipo: "frente",
      file: null,
    });

    await leerDocumentoReceptorConIA(idx, tipo, file);
  };

  const limpiarDocumentoReceptor = (idx, tipo) => {
    const receptor = receptores[idx];
    if (!receptor) return;

    const key = getOcrReceptorKey(receptor, tipo);
    const previewAnterior = ocrReceptores[key]?.previewUrl;

    if (previewAnterior) {
      URL.revokeObjectURL(previewAnterior);
    }

    setOcrReceptores((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const subirDocumentosClienteCompra = async (clienteId) => {
    const errores = [];

    console.log("📤 Intentando subir documentos comprador:", {
      clienteId,
      frente: !!ocrFrenteFile,
      dorso: !!ocrDorsoFile,
    });

    if (!clienteId) return errores;

    if (ocrFrenteFile) {
      try {
        const resp = await subirDocumentoCliente(
          clienteId,
          "frente",
          ocrFrenteFile
        );

        console.log("✅ Frente comprador guardado RESP:", resp);
      } catch (err) {
        console.error(
          "❌ Error subiendo frente comprador:",
          err?.response?.data || err
        );

        errores.push("frente comprador");
      }
    }

    if (ocrDorsoFile) {
      try {
        const resp = await subirDocumentoCliente(
          clienteId,
          "dorso",
          ocrDorsoFile
        );

        console.log("✅ Dorso comprador guardado RESP:", resp);
      } catch (err) {
        console.error(
          "❌ Error subiendo dorso comprador:",
          err?.response?.data || err
        );

        errores.push("dorso comprador");
      }
    }

    return errores;
  };

  const subirDocumentosReceptor = async (receptorTempId, receptorId) => {
    const errores = [];

    const frenteKey = `${receptorTempId}-frente`;
    const dorsoKey = `${receptorTempId}-dorso`;

    const frenteFile = ocrReceptores[frenteKey]?.file || null;
    const dorsoFile = ocrReceptores[dorsoKey]?.file || null;

    console.log("📤 Intentando subir documentos receptor:", {
      receptorTempId,
      receptorId,
      frenteKey,
      dorsoKey,
      frente: !!frenteFile,
      dorso: !!dorsoFile,
    });

    if (!receptorTempId || !receptorId) return errores;

    if (frenteFile) {
      try {
        const resp = await subirDocumentoClienteRecepcion(
          receptorId,
          "frente",
          frenteFile
        );

        console.log("✅ Frente receptor guardado RESP:", resp);
      } catch (err) {
        console.error(
          "❌ Error subiendo frente receptor:",
          err?.response?.data || err
        );

        errores.push("frente receptor");
      }
    }

    if (dorsoFile) {
      try {
        const resp = await subirDocumentoClienteRecepcion(
          receptorId,
          "dorso",
          dorsoFile
        );

        console.log("✅ Dorso receptor guardado RESP:", resp);
      } catch (err) {
        console.error(
          "❌ Error subiendo dorso receptor:",
          err?.response?.data || err
        );

        errores.push("dorso receptor");
      }
    }

    return errores;
  };

  /* ----------------------------- GUARDAR ----------------------------- */

  const guardarVentaRapida = async (e) => {
    e.preventDefault();

    let ventaConfirmada = false;
    let nroVentaConfirmada = "";

    const msg = validar();

    if (msg) {
      alert(msg);
      return;
    }

    const pagosConMonto =
      resumenPagosIniciales
        .pagosCalculados
        .filter(
          (detallePago) =>
            toNumber(
              detallePago
                ?.montoRealUSD,
              0
            ) > 0
        );

    if (pagosConMonto.length === 0) {
      alert(
        "Debe ingresar al menos un pago con un monto mayor a cero antes de guardar la venta."
      );

      setPasoActivo(4);
      return;
    }

    /*
      Si la diferencia financiera es material,
      permitimos guardar pero obligamos
      al vendedor a confirmarla.

      Hasta USD 1 lo consideramos
      redondeo operativo.
    */
    const diferenciasImportantes =
      resumenPagosIniciales
        .pagosCalculados
        .map(
          (
            detallePago,
            idx
          ) => ({
            idx,

            feePreview:
              detallePago
                ?.feePreview,
          })
        )
        .filter(
          (item) =>
            item.feePreview
              ?.esFondeoInverso &&
            Math.abs(
              toNumber(
                item.feePreview
                  ?.diferenciaFinancieraUSD,
                0
              )
            ) > 1
        );

    if (
      diferenciasImportantes.length >
      0
    ) {
      const detalleTexto =
        diferenciasImportantes
          .map((item) => {
            const diferencia =
              round2(
                toNumber(
                  item.feePreview
                    .diferenciaFinancieraUSD,
                  0
                )
              );

            const signo =
              diferencia >= 0
                ? "+"
                : "-";

            return (
              `Pago ${item.idx + 1}: ` +
              `fondeo ${formatUSD(
                item.feePreview
                  .fondeoFinancieraUSD
              )} - ` +
              `diferencia ${signo}${formatUSD(
                Math.abs(
                  diferencia
                )
              )}`
            );
          })
          .join("\n");

      const confirmar =
        window.confirm(
          "Se detectó una diferencia importante en la financiera:\n\n" +
            detalleTexto +
            "\n\nLa venta se guardará igualmente y la diferencia quedará registrada.\n\n¿Desea continuar?"
        );

      if (!confirmar) {
        return;
      }
    }

    try {
      setSaving(true);

      const receptoresUsados =
        estadoRecepcion === "COMPLETA"
          ? new Set(
              items
                .map((item) => item.receptorKey)
                .filter(Boolean)
            )
          : new Set();

      const clienteCompraPayload = {
        apellido1: upper(clienteCompra.apellido1),
        apellido2: upper(clienteCompra.apellido2),
        nombre: upper(clienteCompra.nombre),
        dni: upper(clienteCompra.dni),

        fecing: clienteCompra.fecing || null,

        telefono1: telefonoUSAContinuo(clienteCompra.telefono1),
        email: lower(clienteCompra.email),

        domicilio: upper(clienteCompra.domicilio),
        direccion2: upper(clienteCompra.direccion2),
        ciudad: upper(clienteCompra.ciudad),
        estado: upper(clienteCompra.estado),
        zipCode: upper(clienteCompra.zipCode),
        pais: upper(clienteCompra.pais) || "USA",

        activo: true,
      };

    const clientesRecibenPayload =
      estadoRecepcion === "COMPLETA"
        ? receptoresCalculados
            .filter((receptor) =>
              receptoresUsados.has(receptor.tempId)
            )
            .map((receptor) => {
              const payload = {
                tempId: receptor.tempId,
                codrecep: upper(receptor.codrecep),

                nombre: upper(receptor.nombre),
                segundoNombre: upper(
                  receptor.segundoNombre
                ),
                apellido1: upper(receptor.apellido1),
                apellido2: upper(receptor.apellido2),
                nombreCompleto: upper(
                  receptor.nombreCompleto
                ),

                carnetIdentidad: onlyDigits(
                  receptor.carnetIdentidad
                ),

                telefonoPrincipal:
                  normalizarTelefonoCuba(
                    receptor.telefonoPrincipal
                  ),

                email: lower(receptor.email),

                direccion: upper(receptor.direccion),
                referenciaDireccion: upper(
                  receptor.referenciaDireccion
                ),
                provincia: upper(receptor.provincia),
                municipio: upper(receptor.municipio),
                zonaLogistica: upper(
                  receptor.zonaLogistica
                ),
                pais: upper(receptor.pais) || "CUBA",

                direccionCompleta: upper(
                  receptor.direccionCompleta
                ),

                activo: true,
              };

              if (!upper(payload.codrecep)) {
                delete payload.codrecep;
              }

              return payload;
            })
        : [];

      console.log("📦 Venta rápida - cliente comprador:", clienteCompraPayload);
      console.log("📦 Venta rápida - receptores:", clientesRecibenPayload);
      console.log("📷 DOCUMENTOS A SUBIR:", {
        compradorFrente: !!ocrFrenteFile,
        compradorDorso: !!ocrDorsoFile,
        ocrReceptores,
      });

      const clienteCompraGuardado = await obtenerOCrearClienteCompra(
        clienteCompraPayload
      );

      const erroresDocumentos = [];

      const erroresDocsComprador = await subirDocumentosClienteCompra(
        clienteCompraGuardado._id
      );

      erroresDocumentos.push(...erroresDocsComprador);

      const mapaReceptores = {};
      const mapaReceptoresCompletos = {};

      for (const receptorPayload of clientesRecibenPayload) {
        const receptorGuardado = await obtenerOCrearClienteRecepcion(
          receptorPayload
        );

        mapaReceptores[receptorPayload.tempId] = receptorGuardado._id;

        mapaReceptoresCompletos[receptorPayload.tempId] = {
          ...receptorPayload,
          ...receptorGuardado,
          _id: receptorGuardado._id,
        };

        const erroresDocsReceptor = await subirDocumentosReceptor(
          receptorPayload.tempId,
          receptorGuardado._id
        );

        erroresDocumentos.push(...erroresDocsReceptor);
      }

      const ventaPayload = {
        fecha: venta.fecha,
        clienteCompra: clienteCompraGuardado._id,

        colaboradorVenta: colaboradorVenta || null,

        estado: "PENDIENTE",
        estadoRecepcion,

        observaciones: upper(venta.observaciones),

        /*
          Indica al backend que esta venta pertenece
          a la nueva regla y debe guardar el tax.
        */
        taxAplicado: true,
        
        /*
          El backend calculará Tax únicamente sobre la parte
          pagada con medios que efectivamente llevan Tax.
        */
        taxBaseUSD: resumenPagosIniciales.taxBaseUSD,

        items: items.map((item) => {
          const receptorId =
            estadoRecepcion === "COMPLETA"
              ? mapaReceptores[item.receptorKey]
              : null;

          if (
            estadoRecepcion === "COMPLETA" &&
            !receptorId
          ) {
            throw new Error(
              "No se pudo resolver el receptor de un ítem."
            );
          }

          return {
            producto: item.producto,
            clienteRecibe: receptorId,

            cantidad: toNumber(item.cantidad, 1),
            precioVentaUSD: toNumber(item.precioVentaUSD, 0),
            costoEnvioUSD:
              item.costoEnvioUSD === "" || item.costoEnvioUSD === null
                ? undefined
                : toNumber(item.costoEnvioUSD, 0),

            coloresPreferidos: Array.isArray(item.coloresPreferidos)
              ? item.coloresPreferidos
              : [],

            colorEntregado: item.colorEntregado || "",

            observacionColor: upper(item.observacionColor),

            estadoItem: "PENDIENTE",
            observaciones: upper(item.observaciones),
          };
        }),
      };

      console.log("📦 Venta rápida - payload final venta:", ventaPayload);

      const respVenta = await postVenta(ventaPayload);

      console.log("✅ Venta rápida guardada:", respVenta);

      const ventaGuardada =
        respVenta?.venta || respVenta?.data?.venta || respVenta?.data || null;

      const ventaIdGuardada = ventaGuardada?._id;

      const nro = ventaGuardada?.nroventa ? ` N° ${ventaGuardada.nroventa}` : "";

      ventaConfirmada = true;
      nroVentaConfirmada = nro;

      const pagosAGrabar =
        resumenPagosIniciales.pagosCalculados.filter(
          (detallePago) => detallePago.montoRealUSD > 0
        );

      if (pagosAGrabar.length > 0) {
        if (!ventaIdGuardada) {
          throw new Error(
            "La venta se guardó, pero no se pudo obtener el ID para registrar los pagos."
          );
        }

        for (const detallePago of pagosAGrabar) {
          const {
            pago,
            montoRealUSD,
            feePreview:
              feePreviewCalculado,
          } = detallePago;

        const medioPago = upper(pago.medioPago);

        const usaResponsablePago =
          permiteResponsablePago(medioPago);

        const usaFee =
          medioPago === "CREDITO FINANCIERA" ||
          medioPago === "CREDITO";

        const feePago =
          feePreviewCalculado ||
          calcularFeePagoRapido(
            {
              ...pago,

              montoAplicadoForzadoUSD:
                montoRealUSD,
            },
            financieras
          );

        /*
          Se guarda únicamente la observación escrita
          manualmente por el usuario.
        */
        const observacionesPago = upper(pago.observaciones);

        const pagoPayload = {
          venta: ventaIdGuardada,
          ventaId: ventaIdGuardada,

          medioPago: pago.medioPago,

          /*
            Este es el importe que realmente
            se aplica al saldo de la venta.
          */
          montoUSD: montoRealUSD,

          fechaPago: pago.fechaPago,

          financiera:
            medioPago ===
              "CREDITO FINANCIERA"
              ? pago.financiera
              : null,

          clienteFinanciacion:
            usaResponsablePago
              ? pago.clienteFinanciacion ||
                clienteCompraGuardado._id
              : null,

          cuotasFinanciacion:
            medioPago ===
              "CREDITO FINANCIERA"
              ? toNumber(
                  pago.cuotasFinanciacion,
                  0
                )
              : 0,

          feePorcentaje:
            usaFee
              ? feePago.feePorcentaje
              : 0,

          feeMontoUSD:
            usaFee
              ? feePago.feeMontoUSD
              : 0,

          montoFinancieraUSD:
            medioPago ===
              "CREDITO FINANCIERA"
              ? feePago.montoFinancieraUSD
              : 0,

          totalCobrarClienteUSD:
            esEfectivoEnTienda(
              medioPago
            )
              ? montoRealUSD
              : feePago.totalCobrarClienteUSD,

          estadoAcreditacion:
            pago.estadoAcreditacion,

          fechaAcreditacion:
            pago.estadoAcreditacion ===
              "ACREDITADO"
              ? pago.fechaPago
              : null,

          observaciones:
            observacionesPago,
        };

        /*
          MUY IMPORTANTE:

          Solo FONDEO_INVERSO manda este campo.

          De esta manera no modificamos el
          comportamiento histórico del resto
          de las financieras.
        */
        if (feePago.esFondeoInverso) {
          pagoPayload.fondeoFinancieraUSD =
            feePago.fondeoFinancieraUSD;
        }

        await postPagoVenta(
          pagoPayload
        );
        }
      }

      /*
        Acompañante / garante utilizado
        en la venta en Miami.

        Tomamos el primero de los pagos
        en el que se haya seleccionado
        explícitamente GARANTE.
      */
      const pagoConAcompanante =
        pagosIniciales.find(
          (pago) =>
            permiteResponsablePago(
              pago.medioPago
            ) &&
            pago.financiacionModo ===
              "GARANTE" &&
            getId(
              pago.clienteFinanciacion
            )
        );

      const acompananteMiami =
        pagoConAcompanante
          ? clientesFinanciacion.find(
              (cliente) =>
                getId(cliente) ===
                getId(
                  pagoConAcompanante
                    .clienteFinanciacion
                )
            ) || null
          : null;

      const ventaParaWhatsapp = {
        ...ventaPayload,
        ...(ventaGuardada || {}),
        clienteCompra: clienteCompraGuardado,
        acompananteMiami,
        nroventa: ventaGuardada?.nroventa || ventaPayload.nroventa,
        items: items.map((item) => {
          const productoObj = productosMap[item.producto] || {};
          const receptorObj = mapaReceptoresCompletos[item.receptorKey] || {};

          return {
            producto: productoObj,
            clienteRecibe: receptorObj,
            cantidad: toNumber(item.cantidad, 1),
            precioVentaUSD: toNumber(item.precioVentaUSD, 0),
            costoEnvioUSD: toNumber(item.costoEnvioUSD, 0),
            observaciones: upper(item.observaciones),
          };
        }),
      };

      setVentaWhatsappPendiente(ventaParaWhatsapp);

      setModalVentaOk({
        show: true,
        nro,
        warning:
          erroresDocumentos.length > 0
            ? `La venta se guardó, pero hubo problemas al subir: ${erroresDocumentos.join(
                ", "
              )}.`
            : "",
      });

      /*
        Importante:
        No limpiar automáticamente, porque si limpiamos acá,
        desaparece la venta pendiente para enviar por WhatsApp.
      */
      // limpiarFormulario();
    } catch (err) {
      console.error(
        "❌ Error guardando venta rápida:",
        err?.response?.data || err
      );

      if (ventaConfirmada) {
        console.warn(
          "⚠️ La venta se guardó, pero hubo un error posterior:",
          err?.response?.data || err
        );

        setModalVentaOk({
          show: true,
          nro: nroVentaConfirmada,
          warning:
            "La venta fue grabada. Si apareció un error posterior, no la cargues nuevamente; avisá para revisar pagos/documentos.",
        });

        return;
      }

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo guardar la venta rápida."
      );
    } finally {
      setSaving(false);
    }
  };

  const irPasoAnterior = () => {
    irAPasoVentaRapida(
      Math.max(pasoActivo - 1, 1)
    );
  };

  const irPasoSiguiente = () => {
    irAPasoVentaRapida(
      Math.min(pasoActivo + 1, 4)
    );
  };

  const pasosVentaRapida = [
    {
      numero: 1,
      titulo: "Cliente que Compra",
      subtitulo: "Datos del comprador, documento e información básica.",
      badge: "IA documento",
    },
    {
      numero: 2,
      titulo: "Cliente que Recepciona",
      subtitulo: "Datos del receptor en Cuba, documento y domicilio.",
      badge: "IA documento y domicilio",
    },
    {
      numero: 3,
      titulo: "Elegir Productos",
      subtitulo: "Selección de uno o varios productos de la venta.",
      badge: "Múltiples productos",
    },
    {
      numero: 4,
      titulo: "Pagos / Financiación",
      subtitulo: "Combiná financiera, tienda, tarjeta, zelle o débito.",
      badge: "Pagos múltiples",
    },
  ];

  /* ----------------------------- RENDER ----------------------------- */
  return (
   <>
    <form onSubmit={guardarVentaRapida} className="venta-rapida-wizard">
      <div className="vr-shell">
        <div className="vr-header">
          <div>
            <h2>Venta Rápida</h2>
            <p>Captura en 4 pasos. Menos scroll, más velocidad.</p>
          </div>

          <div className="vr-header-pill">
            Ideal para celular
          </div>
        </div>

        <div className="vr-stepper">
          {pasosVentaRapida.map((paso) => (
            <button
              key={paso.numero}
              type="button"
              className={`vr-step-card ${
                pasoActivo === paso.numero ? "active" : ""
              } ${pasoActivo > paso.numero ? "done" : ""}`}
              onClick={() => irAPasoVentaRapida(paso.numero)}
            >
              <div className="vr-step-number">
                {pasoActivo > paso.numero ? "✓" : paso.numero}
              </div>

              <div className="vr-step-info">
                <h3>
                  {paso.numero}. {paso.titulo}
                </h3>
                <p>{paso.subtitulo}</p>
                <span>{paso.badge}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="vr-info-strip">
          <div>
            <strong>Objetivo:</strong> menos scroll, más eficiencia.
            <span> Flujo optimizado para cargar desde PC o celular.</span>
          </div>

          <div>
            <strong>Podés volver:</strong>
            <span> los datos quedan cargados aunque cambies de paso.</span>
          </div>
</div>

      <div
        className="vr-step-content"
        ref={contenidoPasoRef}
      >
        {pasoActivo === 1 && (
          <div className="vr-step-panel">
              {/* SECCIÓN 1 - CLIENTE COMPRADOR */}
          <div
            className="card mb-3"
            style={{
              backgroundColor: "#eef7ff",
              border: "1px solid #cfe8ff",
            }}
          >
            <div
              className="card-header"
              style={{ backgroundColor: "#d8ecff", fontWeight: 600 }}
            >
              <strong>1. Cliente comprador</strong>
            </div>

            <div className="card-body vr-step-content vr-step-panel-one">

                <div
                  className="mb-3"
                  style={{
                    background: "#f4f7fb",
                    color: "#223044",
                    border: "1px solid #d8e1ec",
                    overflow: "hidden",
                    boxShadow: "0 8px 22px rgba(0,0,0,.10)",
                  }}
                >
                  <div
                    style={{
                      background: "#242d3d",
                      color: "#fff",
                      padding: "14px 18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #344055",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 900,
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        1. Información del comprador
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#b7c0cf",
                          marginTop: 3,
                        }}
                      >
                        Subí el documento, ajustá el recorte y la IA completará los datos.
                      </div>
                    </div>

                    <span
                      style={{
                        background: "#10151f",
                        border: "1px solid #3b465a",
                        color: "#d8dee9",
                        borderRadius: 999,
                        padding: "6px 12px",
                        fontSize: 11,
                        fontWeight: 800,
                        textTransform: "uppercase",
                      }}
                    >
                      OCR / IA
                    </span>
                  </div>

                  <div
                    style={{
                      padding: 18,
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
                      gap: 18,
                    }}
                  >
                    {/* FRENTE */}
                    <div
                      style={{
                        background: "#111722",
                        border: "1px solid #38445a",
                        borderRadius: 12,
                        padding: 16,
                      }}
                    >
                      <div
                        style={{
                          color: "#cbd3df",
                          fontSize: 12,
                          fontWeight: 900,
                          letterSpacing: 0.8,
                          textTransform: "uppercase",
                          marginBottom: 12,
                        }}
                      >
                        ID comprador (frente) *
                      </div>

                      <label
                        style={{
                          minHeight: 145,
                          border: "1px dashed #9b5b2c",
                          borderRadius: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          background: "#151d2a",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          onChange={seleccionarFrenteDocumento}
                          style={{ display: "none" }}
                        />

                        {ocrFrentePreview ? (
                          <img
                            src={ocrFrentePreview}
                            alt="Frente documento comprador"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 135,
                              objectFit: "contain",
                              borderRadius: 8,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              color: "#c7cfda",
                            }}
                          >
                            <div style={{ fontSize: 26, marginBottom: 8 }}>📷</div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: 1,
                                textTransform: "uppercase",
                              }}
                            >
                              Subir fotografía
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                opacity: 0.65,
                                marginTop: 5,
                              }}
                            >
                              JPG, PNG o WEBP
                            </div>
                          </div>
                        )}
                      </label>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 12,
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => leerFrenteDocumentoConIA()}
                          disabled={!ocrFrenteFile || leyendoOcrFrente}
                          style={{
                            flex: 1,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            fontSize: 11,
                          }}
                        >
                          {leyendoOcrFrente ? "Leyendo..." : "Leer con IA"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-light"
                          onClick={limpiarFrenteDocumento}
                          disabled={!ocrFrenteFile && !ocrFrentePreview}
                          style={{
                            flex: 1,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            fontSize: 11,
                          }}
                        >
                          Limpiar
                        </button>
                      </div>

                      {ocrFrenteDatos && (
                        <div
                          style={{
                            marginTop: 12,
                            background: "#143425",
                            border: "1px solid #2c7a55",
                            color: "#d8ffe9",
                            borderRadius: 8,
                            padding: "9px 11px",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Documento leído. Revisá y corregí manualmente si hace falta.
                        </div>
                      )}
                    </div>

                    {/* REVERSO */}
                    <div
                      style={{
                        background: "#111722",
                        border: "1px solid #38445a",
                        borderRadius: 12,
                        padding: 16,
                      }}
                    >
                      <div
                        style={{
                          color: "#cbd3df",
                          fontSize: 12,
                          fontWeight: 900,
                          letterSpacing: 0.8,
                          textTransform: "uppercase",
                          marginBottom: 12,
                        }}
                      >
                        ID comprador (reverso)
                      </div>

                      <label
                        style={{
                          minHeight: 145,
                          border: "1px dashed #9b5b2c",
                          borderRadius: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          background: "#151d2a",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          onChange={seleccionarDorsoDocumento}
                          style={{ display: "none" }}
                        />

                        {ocrDorsoPreview ? (
                          <img
                            src={ocrDorsoPreview}
                            alt="Dorso documento comprador"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 135,
                              objectFit: "contain",
                              borderRadius: 8,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              color: "#c7cfda",
                            }}
                          >
                            <div style={{ fontSize: 26, marginBottom: 8 }}>📷</div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: 1,
                                textTransform: "uppercase",
                              }}
                            >
                              Subir fotografía
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                opacity: 0.65,
                                marginTop: 5,
                              }}
                            >
                              Lectura de domicilio
                            </div>
                          </div>
                        )}
                      </label>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => leerDorsoDocumentoConIA()}
                          disabled={!ocrDorsoFile || leyendoOcrDorso}
                          style={{
                            flex: "1 1 150px",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            fontSize: 11,
                          }}
                        >
                          {leyendoOcrDorso ? "Leyendo..." : "Leer domicilio"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-light"
                          onClick={limpiarDorsoDocumento}
                          disabled={!ocrDorsoFile && !ocrDorsoPreview}
                          style={{
                            flex: "1 1 150px",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            fontSize: 11,
                          }}
                        >
                          Limpiar
                        </button>
                      </div>

                      {ocrDorsoDatos && (
                        <div
                          style={{
                            marginTop: 12,
                            background: "#143425",
                            border: "1px solid #2c7a55",
                            color: "#d8ffe9",
                            borderRadius: 8,
                            padding: "9px 11px",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Dorso leído. Revisá dirección, ciudad, estado, ZIP y país.
                        </div>
                      )}
                    </div>

                  </div>
                </div>

              <h6 className="border-bottom pb-2 mb-3">Datos principales</h6>

              <div className="row g-2">

                <div className="col-md-4">
                  <label className="form-label">Nombre *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="nombre"
                    value={clienteCompra.nombre}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Apellido 1 *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="apellido1"
                    value={clienteCompra.apellido1}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                {/* <div className="col-md-4">
                  <label className="form-label">Apellido 2</label>
                  <input
                    type="text"
                    className="form-control"
                    name="apellido2"
                    value={clienteCompra.apellido2}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div> */}

                <div className="col-md-2">
                  <label className="form-label">ID / Documento *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="dni"
                    value={clienteCompra.dni}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Fecha de ingreso</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fecing"
                    value={clienteCompra.fecing}
                    onChange={handleClienteCompra}
                    disabled={saving}
                  />
                </div>
              </div>

              <h6 className="border-bottom pb-2 mt-4 mb-3">Contacto</h6>

              <div className="row g-2">
                <div className="col-md-4">
                  <div className="vr-phone-highlight">
                    <label className="form-label mb-1">
                      Teléfono cliente
                    </label>

                    <div className="vr-phone-input">
                      <span
                        className="vr-phone-icon"
                        aria-hidden="true"
                      >
                        📞
                      </span>

                      <input
                        type="tel"
                        className="form-control vr-phone-control"
                        name="telefono1"
                        value={formatearTelefonoUSA(
                          clienteCompra.telefono1
                        )}
                        onChange={handleClienteCompra}
                        autoComplete="tel"
                        inputMode="tel"
                        disabled={saving}
                        maxLength={17}
                        placeholder="+1 (000)-000-0000"
                      />

                      <button
                        type="button"
                        className="btn btn-outline-primary vr-phone-copy-btn"
                        onClick={copiarTelefonoCliente}
                        disabled={
                          saving ||
                          getTelefonoUSADigitos(
                            clienteCompra.telefono1
                          ).length !== 10
                        }
                        title="Copiar teléfono sin espacios ni guiones"
                      >
                        {telefonoClienteCopiado
                          ? "✓ Copiado"
                          : "📋 Copiar"}
                      </button>
                    </div>

                    <div className="form-text mt-1">
                      Formato: +1 (000)-000-0000 ·
                      Copiar entrega +10000000000
                    </div>
                  </div>
                </div>

                <div className="col-md-8">
                  <label className="form-label">Correo electrónico (Opcional)</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={clienteCompra.email}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>
              </div>

              <h6 className="border-bottom pb-2 mt-4 mb-3">
                Colaborador de la venta
                <span className="text-muted ms-2 small">
                  (Opcional)
                </span>
              </h6>

              <div className="row g-2 mb-3">
                <div className="col-md-5">
                  <label className="form-label">
                    Buscar colaborador
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    value={qColaborador}
                    onChange={(e) =>
                      setQColaborador(e.target.value)
                    }
                    placeholder="Nombre / apellido / email"
                    disabled={
                      saving ||
                      loadingColaboradores
                    }
                    autoComplete="off"
                  />
                  {clean(qColaborador) &&
                    colaboradoresFiltrados.length > 0 && (
                      <div
                        className="border rounded mt-1"
                        style={{
                          maxHeight: 180,
                          overflowY: "auto",
                          background: "#fff",
                          position: "relative",
                          zIndex: 20,
                        }}
                      >
                        {colaboradoresFiltrados.map(
                          (usuario) => {
                            const nombre = [
                              usuario.nombres ||
                                usuario.nombre,
                              usuario.apellidos ||
                                usuario.apellido,
                            ]
                              .map(clean)
                              .filter(Boolean)
                              .join(" ");

                            return (
                              <button
                                key={usuario._id}
                                type="button"
                                className="btn btn-light w-100 text-start border-0"
                                onClick={() => {
                                  setColaboradorVenta(
                                    usuario._id
                                  );

                                  setQColaborador(
                                    nombre
                                  );
                                }}
                              >
                                <strong>
                                  {nombre || "Sin nombre"}
                                </strong>

                                {usuario.email
                                  ? ` - ${usuario.email}`
                                  : ""}
                              </button>
                            );
                          }
                        )}
                      </div>
                    )}

                </div>

                <div className="col-md-7">
                  <label className="form-label">
                    Colaborador / compañero
                  </label>

                  <select
                    className="form-control"
                    value={colaboradorVenta}
                    onChange={(e) =>
                      setColaboradorVenta(
                        e.target.value
                      )
                    }
                    disabled={
                      saving ||
                      loadingColaboradores
                    }
                  >
                    <option value="">
                      Sin colaborador
                    </option>

                    {colaboradoresFiltrados.map(
                      (usuario) => {
                        const nombre = [
                          usuario.nombres,
                          usuario.apellidos,
                        ]
                          .map(clean)
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <option
                            key={usuario._id}
                            value={usuario._id}
                          >
                            {nombre}
                            {usuario.email
                              ? ` - ${usuario.email}`
                              : ""}
                          </option>
                        );
                      }
                    )}
                  </select>

                  <div className="form-text">
                    Seleccioná únicamente si otra persona
                    colaboró en el cierre de la venta.
                  </div>
                </div>
              </div>

              <h6 className="border-bottom pb-2 mt-4 mb-3">Dirección USA</h6>

              <div className="row g-2">
                <div className="col-md-8">
                  <label className="form-label">Address Line 1</label>
                    <textarea
                      className="form-control"
                      name="domicilio"
                      value={clienteCompra.domicilio}
                      onChange={handleClienteCompra}
                      autoComplete="off"
                      disabled={saving}
                      rows="2"
                      placeholder="Dirección / calle / número / referencias"
                    />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Apt / Suite / Unit</label>
                  <input
                    type="text"
                    className="form-control"
                    name="direccion2"
                    value={clienteCompra.direccion2}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Ciudad</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ciudad"
                    value={clienteCompra.ciudad}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Estado</label>
                  <input
                    type="text"
                    className="form-control"
                    name="estado"
                    value={clienteCompra.estado}
                    onChange={handleClienteCompra}
                    placeholder="Estado / Provincia"
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">ZIP Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="zipCode"
                    value={clienteCompra.zipCode}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">País</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pais"
                    value={clienteCompra.pais}
                    onChange={handleClienteCompra}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          </div>

            </div>
          )}

          {/* SECCIÓN  - PAGOS AGREGADA AL ULTIMO OPCION 3 */}

          {pasoActivo === 4 && (
            <div className="vr-step-panel">
              <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center gap-2">

                  <strong>
                    4. Pagos / Financiación
                  </strong>

                  <div className="d-flex align-items-center gap-2">

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() =>
                        setShowCalculadoraPaso4(
                          true
                        )
                      }
                      disabled={
                        saving
                      }
                    >
                      🧮 Calculadora
                    </button>

                    <button
                      type="button"
                      className="btn vr-btn-agregar-pago"
                      onClick={
                        agregarPagoInicial
                      }
                      disabled={
                        saving
                      }
                    >
                      + Agregar pago
                    </button>

                  </div>

                </div>

                <div className="card-body">
                  <div className="alert alert-info py-2 mb-3">
                    Podés combinar varios medios: financiera, efectivo en tienda, tarjeta,
                    débito o zelle. La suma no debe superar el total de la venta.
                  </div>

                  {pagosIniciales.map((pago, idx) => (
                    <div className="card mb-3" key={pago.tempId}>
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <strong>Pago {idx + 1}</strong>

                        <button
                          type="button"
                          className="btn vr-btn-quitar-pago"
                          onClick={() => quitarPagoInicial(idx)}
                          disabled={saving || pagosIniciales.length <= 1}
                        >
                          ✕ Quitar
                        </button>
                      </div>

                      <div className="card-body">
                        <div className="row g-2">
                          <div className="col-md-3">
                            <label className="form-label">Medio de pago</label>
                            <select
                              className="form-control"
                              name="medioPago"
                              value={pago.medioPago}
                              onChange={(e) => handlePagoInicial(idx, e)}
                              disabled={saving}
                            >
                              {MEDIOS_PAGO_VENTA.map((medio) => (
                                <option key={medio} value={medio}>
                                  {medio}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-2">
                            {(() => {
                              const detallePago =
                                resumenPagosIniciales
                                  .pagosCalculados[idx];

                              const feePreview =
                                detallePago?.feePreview ||
                                calcularFeePagoRapido(
                                  pago,
                                  financieras
                                );

                              const usaFondeoInverso =
                                feePreview
                                  .esFondeoInverso;

                              return (
                                <>
                                  <label className="form-label">
                                    {usaFondeoInverso
                                      ? "Fondeo informado"
                                      : esEfectivoEnTienda(
                                          pago.medioPago
                                        )
                                      ? "Efectivo real recibido"
                                      : esFinanciamientoCasa(
                                          pago.medioPago
                                        )
                                      ? "Monto financiado"
                                      : "Monto aplicado a venta"}
                                  </label>

                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    max={
                                      esFinanciamientoCasa(
                                        pago.medioPago
                                      )
                                        ? LIMITE_FINANCIAMIENTO_CASA_USD
                                        : undefined
                                    }
                                    className="form-control"
                                    name={
                                      usaFondeoInverso
                                        ? "fondeoFinancieraUSD"
                                        : "montoUSD"
                                    }
                                    value={
                                      usaFondeoInverso
                                        ? pago.fondeoFinancieraUSD
                                        : pago.montoUSD
                                    }
                                    onChange={(e) =>
                                      handlePagoInicial(
                                        idx,
                                        e
                                      )
                                    }
                                    disabled={saving}
                                    placeholder="0.00"
                                  />

                                {esFinanciamientoCasa(
                                    pago.medioPago
                                  ) && (
                                    <div
                                      className="mt-1 fw-bold"
                                      style={{
                                        fontSize: 12,
                                        color: "#60a5fa",
                                      }}
                                    >
                                      Máximo financiable: US$ 1.000,00
                                      · Sin Tax · Sin Fee · Queda pendiente
                                    </div>
                                  )}

                                  {usaFondeoInverso &&
                                    feePreview
                                      .fondeoFinancieraUSD >
                                      0 && (
                                      <div className="mt-1">
                                        <div className="small text-danger fw-bold">
                                          Fee{" "}
                                          {
                                            feePreview
                                              .feePorcentaje
                                          }
                                          %:{" "}
                                          {formatUSD(
                                            feePreview
                                              .feeMontoUSD
                                          )}
                                        </div>

                                        <div className="small text-success fw-bold">
                                          Aplicado a venta:{" "}
                                          {formatUSD(
                                            feePreview
                                              .montoAplicadoUSD
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </>
                              );
                            })()}

                            {(() => {
                              const detallePago =
                                resumenPagosIniciales
                                  .pagosCalculados[idx];

                              const feePreview =
                                detallePago?.feePreview ||
                                calcularFeePagoRapido(
                                  pago,
                                  financieras
                                );

                              const pagoSinTax =
                                esPagoSinTax(
                                  pago,
                                  financieras
                                );

                              const saldoNormalUSD =
                                round2(
                                  Math.max(
                                    resumenPagosIniciales
                                      .saldoPendienteUSD,
                                    0
                                  )
                                );

                              const saldoSinTaxUSD =
                                round2(
                                  Math.max(
                                    saldoPendienteSinTaxUSD,
                                    0
                                  )
                                );

                                const esFondeoInverso =
                                  Boolean(
                                    feePreview?.esFondeoInverso
                                  );

                                const fondeoInformadoUSD =
                                  round2(
                                    Math.max(
                                      0,
                                      toNumber(
                                        feePreview?.fondeoFinancieraUSD,
                                        0
                                      )
                                    )
                                  );

                                const fondeoEsperadoVisualUSD =
                                  getFondeoEsperadoVisualUSD(
                                    pago,
                                    feePreview
                                  );

                                const faltanteFondeoUSD =
                                  esFondeoInverso
                                    ? round2(
                                        Math.max(
                                          fondeoEsperadoVisualUSD -
                                            fondeoInformadoUSD,
                                          0
                                        )
                                      )
                                    : 0;

                                const fondeoInversoCompleto =
                                  esFondeoInverso &&
                                  fondeoInformadoUSD > 0 &&
                                  round2(fondeoInformadoUSD) ===
                                    round2(fondeoEsperadoVisualUSD);

                                const ventaCompleta =
                                  esFondeoInverso
                                    ? fondeoInversoCompleto
                                    : saldoNormalUSD <= 0.01;

                              return (
                                <div
                                  className="mt-2 p-2 rounded vr-pago-predictivo"
                                  style={{
                                    background:
                                      ventaCompleta
                                        ? "#ecfdf5"
                                        : "#eff6ff",

                                    border:
                                      ventaCompleta
                                        ? "2px solid #22c55e"
                                        : "2px solid #3b82f6",

                                    textAlign: "center",
                                  }}
                                >
                                  {ventaCompleta ? (
                                    <>
                                      <div
                                        style={{
                                          color: "#15803d",
                                          fontSize: 13,
                                          fontWeight: 900,
                                        }}
                                      >
                                        ✓ PAGO COMPLETO
                                      </div>

                                      <div
                                        style={{
                                          color: "#15803d",
                                          fontSize: 20,
                                          fontWeight: 900,
                                          marginTop: 2,
                                        }}
                                      >
                                        {formatUSD(0)}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div
                                        style={{
                                          color: "#1e40af",
                                          fontSize: 12,
                                          fontWeight: 900,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        Falta completar la venta
                                      </div>

                                      <div
                                        style={{
                                          color: "#1d4ed8",
                                          fontSize: 22,
                                          fontWeight: 900,
                                          lineHeight: 1.15,
                                          marginTop: 3,
                                        }}
                                      >
                                        {esFondeoInverso
                                          ? formatUSD(
                                              fondeoEsperadoVisualUSD
                                            )
                                          : formatUSD(
                                              saldoNormalUSD
                                            )}
                                      </div>

                                      {pagoSinTax && !feePreview?.esFondeoInverso && (
                                        <div
                                          style={{
                                            marginTop: 6,
                                            paddingTop: 6,
                                            borderTop:
                                              "1px solid #bfdbfe",
                                            color: "#166534",
                                            fontSize: 13,
                                            fontWeight: 800,
                                          }}
                                        >
                                          Este medio NO lleva Tax
                                          <br />

                                          <span
                                            style={{
                                              fontSize: 16,
                                            }}
                                          >
                                            Cargar para completar:{" "}
                                            {formatUSD(
                                              saldoSinTaxUSD
                                            )}
                                          </span>
                                        </div>
                                      )}

                                      {!pagoSinTax &&
                                        !feePreview
                                          ?.esFondeoInverso && (
                                          <div
                                            style={{
                                              marginTop: 6,
                                              color: "#334155",
                                              fontSize: 13,
                                              fontWeight: 700,
                                            }}
                                          >
                                            Cargar para completar:{" "}
                                            <strong>
                                              {formatUSD(
                                                saldoNormalUSD
                                              )}
                                            </strong>
                                          </div>
                                        )}

                                        {feePreview?.esFondeoInverso && (
                                          <div
                                            style={{
                                              marginTop: 6,
                                              color: "#334155",
                                              fontSize: 12,
                                              fontWeight: 700,
                                            }}
                                          >
                                            Fondeo informado:{" "}
                                            <strong>
                                              {formatUSD(
                                                feePreview.fondeoFinancieraUSD || 0
                                              )}
                                            </strong>
                                            <br />

                                            Fondeo esperado:{" "}
                                            <strong>
                                              {formatUSD(
                                                fondeoEsperadoVisualUSD
                                              )}
                                            </strong>

                                            {fondeoInformadoUSD > 0 &&
                                              faltanteFondeoUSD > 0 && (
                                                <>
                                                  <br />

                                                  <span
                                                    style={{
                                                      color: "#dc2626",
                                                      fontWeight: 900,
                                                    }}
                                                  >
                                                    Falta fondear:{" "}
                                                    {formatUSD(
                                                      faltanteFondeoUSD
                                                    )}
                                                  </span>
                                                </>
                                              )}
                                          </div>
                                        )}
                                    </>
                                  )}
                                </div>
                              );
                            })()}

                            {resumenPagosIniciales.pagosCalculados[idx]
                              ?.pagoSinTax &&
                              resumenPagosIniciales.pagosCalculados[idx]
                                ?.devolucionTaxUSD > 0 && (
                                <div
                                  className="small fw-bold mt-1"
                                  style={{ color: "#16a34a" }}
                                >
                                  {esEfectivoEnTienda(
                                    pago.medioPago
                                  )
                                    ? "Tax no aplicado por efectivo: "
                                    : esFinanciamientoCasa(
                                        pago.medioPago
                                      )
                                    ? "Tax no aplicado por financiamiento con la casa: "
                                    : "Tax no aplicado por financiera: "}

                                  {formatUSD(
                                    resumenPagosIniciales.pagosCalculados[idx]
                                      .devolucionTaxUSD
                                  )}
                                </div>
                              )}

                            {esEfectivoEnTienda(pago.medioPago) &&
                              resumenPagosIniciales.pagosCalculados[idx]
                                ?.montoAplicadoUSD > 0 && (
                                <div className="small text-muted">
                                  Aplicado a venta:{" "}
                                  {formatUSD(
                                    resumenPagosIniciales.pagosCalculados[idx]
                                      .montoAplicadoUSD
                                  )}
                                </div>
                              )}
                          </div>

                          <div className="col-md-2">
                            <label className="form-label">Fecha pago</label>
                            <input
                              type="date"
                              className="form-control"
                              name="fechaPago"
                              value={pago.fechaPago}
                              onChange={(e) => handlePagoInicial(idx, e)}
                              disabled={saving}
                            />
                          </div>

                          {/* <div className="col-md-2">
                            <label className="form-label">Estado</label>
                            <select
                              className="form-control"
                              name="estadoAcreditacion"
                              value={pago.estadoAcreditacion}
                              onChange={(e) => handlePagoInicial(idx, e)}
                              disabled={saving}
                            >
                              {ESTADOS_ACREDITACION_PAGO.map((estado) => (
                                <option key={estado} value={estado}>
                                  {estado}
                                </option>
                              ))}
                            </select>
                          </div> */}

                          {/* <div className="col-md-3">
                            <label className="form-label">Fecha acreditación</label>
                            <input
                              type="date"
                              className="form-control"
                              name="fechaAcreditacion"
                              value={pago.fechaAcreditacion}
                              onChange={(e) => handlePagoInicial(idx, e)}
                              disabled={saving}
                            />
                          </div> */}

                          {pago.medioPago === "CREDITO FINANCIERA" && (
                            <>
                              <div className="col-md-3">
                                <label className="form-label">Financiera *</label>
                                <select
                                  className="form-control"
                                  name="financiera"
                                  value={pago.financiera}
                                  onChange={(e) => handlePagoInicial(idx, e)}
                                  disabled={saving}
                                >
                                  <option value="">Seleccione financiera</option>

                                  {financieras.map((f) => {
                                    const id = f._id || f.id;
                                    const nombre =
                                      clean(f.financiera) ||
                                      clean(f.razonsocial) ||
                                      `Financiera ${f.codfinanciera || ""}`;

                                    return (
                                      <option key={id} value={id}>
                                        {nombre}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>

                              <div className="col-md-1">
                                <label className="form-label d-none d-md-block">&nbsp;</label>

                                <div className="vr-financiera-logo-box-inline">
                                  {getLogoFinanciera(pago.financiera, financieras) ? (
                                    <img
                                      src={getLogoFinanciera(pago.financiera, financieras)}
                                      alt="Logo financiera"
                                      className="vr-financiera-logo-inline"
                                    />
                                  ) : (
                                    <span className="vr-financiera-logo-empty">-</span>
                                  )}
                                </div>
                              </div>

                            <div className="col-md-8">
                              {(() => {
                                const modoFinanciacion =
                                  pago.financiacionModo ||
                                  (pago.clienteFinanciacion ? "GARANTE" : "COMPRADOR");

                                const nombreComprador = [
                                  clienteCompra.apellido1,
                                  clienteCompra.apellido2,
                                  clienteCompra.nombre,
                                ]

                                  .map(clean)
                                  .filter(Boolean)
                                  .join(" ")
                                  .replace(/\s+/g, " ")
                                  .trim();

                                const detallePago =
                                  resumenPagosIniciales
                                    .pagosCalculados[idx];

                                const feePreview =
                                  detallePago?.feePreview ||
                                  calcularFeePagoRapido(
                                    pago,
                                    financieras
                                  );

                                const fondeoEsperadoVisualUSD =
                                  getFondeoEsperadoVisualUSD(
                                    pago,
                                    feePreview
                                  );

                                return (
                                  <>
                                    <div className="vr-financiera-calculo mb-2">
                                      {feePreview.requiereCuotas && (
                                        <div className="vr-financiera-calculo-item">
                                          <span>Cuotas</span>

                                          <select
                                            className="form-control form-control-sm"
                                            name="cuotasFinanciacion"
                                            value={pago.cuotasFinanciacion}
                                            onChange={(e) => handlePagoInicial(idx, e)}
                                            disabled={saving}
                                          >
                                          <option value="">
                                            Elegir
                                          </option>

                                          {feePreview
                                            .cuotasDisponibles
                                            .map((cantidad) => (
                                              <option
                                                key={cantidad}
                                                value={cantidad}
                                              >
                                                {cantidad} cuotas
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      )}

                                      <div className="vr-financiera-calculo-item">
                                        <span>Tarifa</span>

                                        <strong>
                                          {feePreview.feePorcentaje}%
                                        </strong>
                                      </div>

                                      <div className="vr-financiera-calculo-item">
                                        <span>Fee Financiera</span>
                                        <strong>{formatUSD(feePreview.feeMontoUSD)}</strong>
                                      </div>

                                      <div className="vr-financiera-calculo-item">
                                        <span>Tax</span>

                                        <strong>
                                          {formatUSD(
                                            detallePago?.taxCobradoUSD || 0
                                          )}
                                        </strong>
                                      </div>

                                      {feePreview.esFondeoInverso && (
                                        <>
                                          <div className="vr-financiera-calculo-item">
                                            <span>
                                              Neto financiera
                                            </span>

                                            <strong>
                                              {formatUSD(
                                                feePreview
                                                  .montoFinancieraUSD
                                              )}
                                            </strong>
                                          </div>

                                          <div className="vr-financiera-calculo-item">
                                            <span>
                                              Fondeo esperado
                                            </span>

                                            <strong>
                                              {formatUSD(
                                                fondeoEsperadoVisualUSD
                                              )}
                                            </strong>
                                          </div>

                                          <div className="vr-financiera-calculo-item">
                                            <span>
                                              Diferencia financiera
                                            </span>

                                            <strong
                                              style={{
                                                color:
                                                  Math.abs(
                                                    toNumber(
                                                      feePreview
                                                        .diferenciaFinancieraUSD,
                                                      0
                                                    )
                                                  ) > 1
                                                    ? "#dc2626"
                                                    : "#d97706",
                                              }}
                                            >
                                              {toNumber(
                                                feePreview
                                                  .diferenciaFinancieraUSD,
                                                0
                                              ) > 0
                                                ? "+"
                                                : ""}

                                              {formatUSD(
                                                feePreview
                                                  .diferenciaFinancieraUSD
                                              )}
                                            </strong>
                                          </div>

                                          {toNumber(
                                            feePreview
                                              .ajusteFinancieroUSD,
                                            0
                                          ) > 0 && (
                                            <div className="vr-financiera-calculo-item">
                                              <span>
                                                Ajuste financiero
                                              </span>

                                              <strong
                                                style={{
                                                  color: "#dc2626",
                                                }}
                                              >
                                                {formatUSD(
                                                  feePreview
                                                    .ajusteFinancieroUSD
                                                )}
                                              </strong>
                                            </div>
                                          )}

                                          <div className="vr-financiera-calculo-item">
                                            <span>
                                              Aplicado a venta
                                            </span>

                                            <strong>
                                              {formatUSD(
                                                feePreview
                                                  .montoAplicadoUSD
                                              )}
                                            </strong>
                                          </div>
                                        </>
                                      )}

                                      {(() => {
                                        const tipoCalculoActual =
                                          upper(
                                            feePreview
                                              ?.tipoCalculo
                                          );

                                        const permiteTotalCobradoManual =
                                          !feePreview
                                            ?.esFondeoInverso &&
                                          [
                                            "GROSS_UP",
                                            "POR_CUOTA",
                                          ].includes(
                                            tipoCalculoActual
                                          );

                                        /*
                                          FONDEO INVERSO:
                                          queda EXACTAMENTE como está.
                                        */
                                        if (
                                          feePreview
                                            ?.esFondeoInverso
                                        ) {
                                          return (
                                            <div className="vr-financiera-calculo-item destacado">
                                              <span>
                                                Total Fondeado
                                              </span>

                                              <strong>
                                                {formatUSD(
                                                  feePreview
                                                    .totalCobrarClienteUSD
                                                )}
                                              </strong>
                                            </div>
                                          );
                                        }

                                        /*
                                          GROSS UP:
                                          Total Cobrado pasa a ser editable.
                                        */
                                        if (
                                          permiteTotalCobradoManual
                                        ) {
                                          return (
                                            <div className="vr-financiera-calculo-item destacado">
                                              <span>
                                                Total Fondeado
                                              </span>

                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="form-control form-control-sm mt-1"
                                                name="totalCobradoManualUSD"
                                                value={
                                                  pago.origenCalculoFinanciera ===
                                                  "TOTAL_COBRADO"
                                                    ? pago.totalCobradoManualUSD
                                                    : feePreview
                                                          .totalCobrarClienteUSD >
                                                        0
                                                    ? feePreview
                                                        .totalCobrarClienteUSD
                                                    : ""
                                                }
                                                onChange={(e) =>
                                                  handlePagoInicial(
                                                    idx,
                                                    e
                                                  )
                                                }
                                                disabled={saving}
                                                placeholder="0.00"
                                              />
                                            </div>
                                          );
                                        }

                                        /*
                                          Cualquier otra modalidad:
                                          solo lectura como hasta ahora.
                                        */
                                        return (
                                          <div className="vr-financiera-calculo-item destacado">
                                            <span>
                                              Total Fondeado
                                            </span>

                                            <strong>
                                              {formatUSD(
                                                feePreview
                                                  .totalCobrarClienteUSD
                                              )}
                                            </strong>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                                      <label className="form-label mb-0">
                                        ¿Quién financia este pago?
                                      </label>

                                      {modoFinanciacion === "GARANTE" && (
                                        <button
                                          type="button"
                                          className="btn btn-success btn-sm vr-btn-nuevo-ia"
                                          onClick={() => abrirModalGaranteFinanciacion(idx)}
                                          disabled={saving}
                                        >
                                          + Nuevo garante IA
                                        </button>
                                      )}
                                    </div>

                                    <div className="vr-financiador-switch mb-2">
                                      <button
                                        type="button"
                                        className={
                                          modoFinanciacion === "COMPRADOR"
                                            ? "vr-financiador-option active"
                                            : "vr-financiador-option"
                                        }
                                        onClick={() =>
                                          handlePagoInicial(idx, {
                                            target: {
                                              name: "financiacionModo",
                                              value: "COMPRADOR",
                                            },
                                          })
                                        }
                                        disabled={saving}
                                      >
                                        Cliente comprador
                                      </button>

                                      <button
                                        type="button"
                                        className={
                                          modoFinanciacion === "GARANTE"
                                            ? "vr-financiador-option active garante"
                                            : "vr-financiador-option garante"
                                        }
                                        onClick={() =>
                                          handlePagoInicial(idx, {
                                            target: {
                                              name: "financiacionModo",
                                              value: "GARANTE",
                                            },
                                          })
                                        }
                                        disabled={saving}
                                      >
                                        Acompañante / garante
                                      </button>
                                    </div>

                                    {modoFinanciacion === "COMPRADOR" ? (
                                      <div className="vr-financiador-resumen comprador">
                                        <strong>Financia el cliente comprador:</strong>{" "}
                                        {nombreComprador || "Cliente comprador"}
                                      </div>
                                    ) : (
                                      <>
                                        <input
                                          type="text"
                                          className="form-control mb-1"
                                          value={qClienteFinanciacion}
                                          onChange={(e) => setQClienteFinanciacion(e.target.value)}
                                          disabled={saving}
                                          placeholder="Buscar garante o comprador por nombre / documento / teléfono / email"
                                        />

                                        <select
                                          className="form-control"
                                          name="clienteFinanciacion"
                                          value={pago.clienteFinanciacion}
                                          onChange={(e) => handlePagoInicial(idx, e)}
                                          disabled={saving}
                                        >
                                          <option value="">Seleccione acompañante / garante</option>

                                          {clientesFinanciacionFiltrados.map((c) => {
                                            const id = c._id || c.id;

                                            const nombreArmado = [
                                              c.apellido1,
                                              c.apellido2,
                                              c.nombre,
                                            ]
                                              .map(clean)
                                              .filter(Boolean)
                                              .join(" ")
                                              .replace(/\s+/g, " ")
                                              .trim();

                                            const nombre =
                                              nombreArmado ||
                                              clean(c.razonsocial) ||
                                              clean(c.dni) ||
                                              "Cliente";

                                            const tel = clean(
                                              c.telefono1 || c.telefonoPrincipal || c.telefono
                                            );

                                            const marcaGarante = c.esGarante ? " GARANTE" : "";

                                            return (
                                              <option key={id} value={id}>
                                                {nombre}
                                                {tel ? ` - ${tel}` : ""}
                                                {marcaGarante}
                                              </option>
                                            );
                                          })}
                                        </select>

                                        <div className="vr-financiador-ayuda">
                                          Si no está en la lista, usá el botón verde{" "}
                                          <strong>+ Nuevo garante IA</strong>.
                                        </div>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            </>
                          )}

                          {["CREDITO", "DEBITO"].includes(
                            upper(pago.medioPago)
                          ) && (
                            <div className="col-md-12">
                              {(() => {
                                const modoFinanciacion =
                                  pago.financiacionModo ||
                                  (pago.clienteFinanciacion
                                    ? "GARANTE"
                                    : "COMPRADOR");

                                const nombreComprador = [
                                  clienteCompra.apellido1,
                                  clienteCompra.apellido2,
                                  clienteCompra.nombre,
                                ]
                                  .map(clean)
                                  .filter(Boolean)
                                  .join(" ")
                                  .replace(/\s+/g, " ")
                                  .trim();

                                const feePreview =
                                  calcularFeePagoRapido(
                                    pago,
                                    financieras
                                  );

                                const esCredito =
                                  upper(pago.medioPago) ===
                                  "CREDITO";

                                return (
                                  <>
                                    {/* CÁLCULO TARJETA */}
                                    <div className="vr-financiera-calculo mb-2">
                                      <div className="vr-financiera-calculo-item">
                                        <span>Tarifa</span>

                                        <strong>
                                          {feePreview.feePorcentaje}%
                                        </strong>
                                      </div>

                                      <div className="vr-financiera-calculo-item">
                                        <span>
                                          {esCredito
                                            ? "Fee Tarjeta"
                                            : "Fee Débito"}
                                        </span>

                                        <strong>
                                          {formatUSD(
                                            feePreview.feeMontoUSD
                                          )}
                                        </strong>
                                      </div>

                                      <div className="vr-financiera-calculo-item destacado">
                                        <span>Total Cobrado</span>

                                        <strong>
                                          {formatUSD(
                                            feePreview.totalCobrarClienteUSD
                                          )}
                                        </strong>
                                      </div>
                                    </div>

                                    {/* QUIÉN REALIZA EL PAGO */}
                                    <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                                      <label className="form-label mb-0">
                                        ¿Quién realiza este pago?
                                      </label>

                                      {modoFinanciacion === "GARANTE" && (
                                        <button
                                          type="button"
                                          className="btn btn-success btn-sm vr-btn-nuevo-ia"
                                          onClick={() =>
                                            abrirModalGaranteFinanciacion(idx)
                                          }
                                          disabled={saving}
                                        >
                                          + Nuevo garante IA
                                        </button>
                                      )}
                                    </div>

                                    <div className="vr-financiador-switch mb-2">
                                      <button
                                        type="button"
                                        className={
                                          modoFinanciacion === "COMPRADOR"
                                            ? "vr-financiador-option active"
                                            : "vr-financiador-option"
                                        }
                                        onClick={() =>
                                          handlePagoInicial(idx, {
                                            target: {
                                              name: "financiacionModo",
                                              value: "COMPRADOR",
                                            },
                                          })
                                        }
                                        disabled={saving}
                                      >
                                        Cliente comprador
                                      </button>

                                      <button
                                        type="button"
                                        className={
                                          modoFinanciacion === "GARANTE"
                                            ? "vr-financiador-option active garante"
                                            : "vr-financiador-option garante"
                                        }
                                        onClick={() =>
                                          handlePagoInicial(idx, {
                                            target: {
                                              name: "financiacionModo",
                                              value: "GARANTE",
                                            },
                                          })
                                        }
                                        disabled={saving}
                                      >
                                        Acompañante / garante
                                      </button>
                                    </div>

                                    {modoFinanciacion === "COMPRADOR" ? (
                                      <div className="vr-financiador-resumen comprador">
                                        <strong>
                                          Realiza el pago el cliente comprador:
                                        </strong>{" "}
                                        {nombreComprador ||
                                          "Cliente comprador"}
                                      </div>
                                    ) : (
                                      <>
                                        <input
                                          type="text"
                                          className="form-control mb-1"
                                          value={qClienteFinanciacion}
                                          onChange={(e) =>
                                            setQClienteFinanciacion(
                                              e.target.value
                                            )
                                          }
                                          disabled={saving}
                                          placeholder="Buscar acompañante / garante por nombre, DNI, teléfono o email"
                                        />

                                        <select
                                          className="form-control"
                                          name="clienteFinanciacion"
                                          value={
                                            pago.clienteFinanciacion
                                          }
                                          onChange={(e) =>
                                            handlePagoInicial(idx, e)
                                          }
                                          disabled={saving}
                                        >
                                          <option value="">
                                            Seleccione acompañante / garante
                                          </option>

                                          {clientesFinanciacionFiltrados.map(
                                            (c) => {
                                              const id =
                                                c._id || c.id;

                                              const nombreArmado = [
                                                c.apellido1,
                                                c.apellido2,
                                                c.nombre,
                                              ]
                                                .map(clean)
                                                .filter(Boolean)
                                                .join(" ")
                                                .replace(/\s+/g, " ")
                                                .trim();

                                              const nombre =
                                                nombreArmado ||
                                                clean(c.razonsocial) ||
                                                clean(c.dni) ||
                                                "Cliente";

                                              const dni = clean(
                                                c.dni ||
                                                  c.documento ||
                                                  c.idDocumento
                                              );

                                              const tel = clean(
                                                c.telefono1 ||
                                                  c.telefonoPrincipal ||
                                                  c.telefono
                                              );

                                              const marcaGarante =
                                                c.esGarante
                                                  ? " GARANTE"
                                                  : "";

                                              return (
                                                <option
                                                  key={id}
                                                  value={id}
                                                >
                                                  {nombre}
                                                  {dni
                                                    ? ` - DNI: ${dni}`
                                                    : ""}
                                                  {tel
                                                    ? ` - ${tel}`
                                                    : ""}
                                                  {marcaGarante}
                                                </option>
                                              );
                                            }
                                          )}
                                        </select>

                                        <div className="vr-financiador-ayuda">
                                          Podés buscarlo por{" "}
                                          <strong>DNI</strong>, nombre,
                                          teléfono o email. Si no existe,
                                          usá{" "}
                                          <strong>
                                            + Nuevo garante IA
                                          </strong>.
                                        </div>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          <div className="col-md-12">
                            <label className="form-label">Observaciones</label>
                            <input
                              type="text"
                              className="form-control"
                              name="observaciones"
                              value={pago.observaciones}
                              onChange={(e) => handlePagoInicial(idx, e)}
                              disabled={saving}
                              placeholder="Observaciones del pago"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="card mt-3">
                    <div className="card-header">
                      <strong>Resumen de pagos</strong>
                    </div>

                    <div className="row g-2">

                      {/* FILA 1 */}

                      <div className="col-md-3">
                        <div
                            className="border rounded p-2 bg-white h-100"
                            style={{
                              borderWidth: 2,
                              borderColor: "#38bdf8",
                              boxShadow:
                                 "inset 0 0 0 9999px rgba(125, 211, 252, 0.16)",
                            }}
                            >
                            <div
                              className="small fw-bold"
                              style={{
                                color: "#38bdf8",
                              }}
                            >
                              Venta neta
                            </div>

                            <strong
                              style={{
                                color: "#38bdf8",
                                fontSize: 16,
                                fontWeight: 800,
                              }}
                            >
                            {formatUSD(
                              round2(
                                totales.totalProductosUSD +
                                  totales.totalEnvioUSD
                              )
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Total venta con Tax
                          </div>

                          <strong>
                            {formatUSD(totales.totalUSD)}
                          </strong>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Total real de la venta
                          </div>

                          <strong>
                            {formatUSD(
                              resumenPagosIniciales.totalRealCobrarUSD
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Total real cobrado al cliente
                          </div>

                          <strong>
                            {formatUSD(
                              resumenPagosIniciales.totalCobrarClienteUSD
                            )}
                          </strong>

                          {resumenPagosIniciales.totalFeeClienteUSD > 0 && (
                            <div className="small text-danger fw-bold">
                              Incluye fee / recargo:{" "}
                              {formatUSD(
                                resumenPagosIniciales.totalFeeClienteUSD
                              )}
                            </div>
                          )}
                        </div>
                      </div>


                      {/* FILA 2 */}

                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Aplicado a la venta
                          </div>

                          <strong>
                            {formatUSD(
                              resumenPagosIniciales.totalPagosUSD
                            )}
                          </strong>

                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Tax cobrado
                          </div>

                          <strong style={{ color: "#16a34a" }}>
                            {formatUSD(
                              resumenPagosIniciales.totalTaxCobradoUSD
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Saldo pendiente
                          </div>

                          <strong
                            style={{
                              color:
                                resumenPagosIniciales.saldoPendienteUSD > 0
                                  ? "#f59e0b"
                                  : "#22c55e",
                            }}
                          >
                            {formatUSD(
                              Math.max(
                                resumenPagosIniciales.saldoPendienteUSD,
                                0
                              )
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="border rounded p-2 bg-white h-100">
                          <div className="text-muted small">
                            Saldo pendiente sin Tax
                          </div>

                          <strong
                            style={{
                              color:
                                saldoPendienteSinTaxUSD > 0
                                  ? "#3b82f6"
                                  : "#22c55e",
                            }}
                          >
                            {formatUSD(
                              saldoPendienteSinTaxUSD
                            )}
                          </strong>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {pasoActivo === 2 && (
            <div className="vr-step-panel">
              {/* SECCIÓN 2 - CLIENTES RECEPTORES */}
          <div
            className="card mb-3"
            style={{
              backgroundColor: "#eefcf1",
              border: "1px solid #cfeecf",
            }}
          >
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{
                backgroundColor: "#dff5e3",
                fontWeight: 600,
              }}
            >
              <strong>
                2. Cliente que recepciona en Cuba
              </strong>

              {estadoRecepcion === "COMPLETA" && (
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={agregarReceptor}
                  disabled={saving}
                >
                  Agregar receptor
                </button>
              )}
            </div>

            <div
              className="alert alert-primary m-3"
              style={{ marginBottom: 16 }}
            >
              <div className="fw-bold mb-2">
                ¿Tenés ahora los datos del receptor?
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className={
                    estadoRecepcion === "COMPLETA"
                      ? "btn btn-success"
                      : "btn btn-outline-success"
                  }
                  onClick={() =>
                    setEstadoRecepcion("COMPLETA")
                  }
                  disabled={saving}
                >
                  ✓ Cargar receptor ahora
                </button>

                <button
                  type="button"
                  className={
                    estadoRecepcion === "PENDIENTE"
                      ? "btn btn-warning text-primary"
                      : "btn btn-outline-warning text-primary"
                  }
                  onClick={() =>
                    setEstadoRecepcion("PENDIENTE")
                  }
                  disabled={saving}
                >
                  ⏳ Completar posteriormente
                </button>
              </div>

              {estadoRecepcion === "PENDIENTE" && (
                <div
                  className="mt-3 fw-bold"
                  style={{ color: "#92400e" }}
                >
                  ⏳ La venta podrá cerrarse ahora sin receptor.
                  Los datos deberán completarse posteriormente
                  desde el listado de ventas.
                </div>
              )}
            </div>


           {estadoRecepcion === "COMPLETA" && (
            <div className="vr-step-content">
              {receptores.map((receptor, idx) => {
                const calculado = receptoresCalculados[idx] || receptor;

                const ocrReceptorFrente =
                  ocrReceptores[getOcrReceptorKey(receptor, "frente")] || {};

                const ocrReceptorDorso =
                  ocrReceptores[getOcrReceptorKey(receptor, "dorso")] || {};

                return (
                  <div className="card mb-3" key={receptor.tempId}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <strong>Receptor {idx + 1}</strong>

                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => quitarReceptor(idx)}
                        disabled={saving || receptores.length <= 1}
                      >
                        Quitar
                      </button>
                    </div>

                    <div className="card-body">

                      <div
                        className="mb-3"
                        style={{
                          background: "#f4f7fb",
                          color: "#223044",
                          border: "1px solid #d8e1ec",
                          overflow: "hidden",
                          boxShadow: "0 8px 22px rgba(0,0,0,.08)",
                        }}
                      >
                        <div
                          style={{
                            background: "#242d3d",
                            color: "#fff",
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: "1px solid #344055",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: 0.5,
                                textTransform: "uppercase",
                              }}
                            >
                              Documento del receptor en Cuba
                            </div>

                            <div
                              style={{
                                fontSize: 12,
                                color: "#b7c0cf",
                                marginTop: 3,
                              }}
                            >
                              Frente para identidad, reverso para dirección.
                            </div>
                          </div>

                          <span
                            style={{
                              background: "#10151f",
                              border: "1px solid #3b465a",
                              color: "#d8dee9",
                              borderRadius: 999,
                              padding: "6px 12px",
                              fontSize: 11,
                              fontWeight: 800,
                              textTransform: "uppercase",
                            }}
                          >
                            OCR / IA
                          </span>
                        </div>

                        <div
                          style={{
                            padding: 16,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                            gap: 16,
                          }}
                        >
                          {/* FRENTE RECEPTOR */}
                          <div
                            style={{
                              background: "#111722",
                              border: "1px solid #38445a",
                              borderRadius: 12,
                              padding: 16,
                            }}
                          >
                            <div
                              style={{
                                color: "#cbd3df",
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: 0.8,
                                textTransform: "uppercase",
                                marginBottom: 12,
                              }}
                            >
                              ID receptor (frente)
                            </div>

                            <label
                              style={{
                                minHeight: 130,
                                border: "1px dashed #9b5b2c",
                                borderRadius: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                background: "#151d2a",
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => seleccionarDocumentoReceptor(idx, "frente", e)}
                                style={{ display: "none" }}
                              />

                              {ocrReceptorFrente.previewUrl ? (
                                <img
                                  src={ocrReceptorFrente.previewUrl}
                                  alt="Frente documento receptor"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: 120,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                  }}
                                />
                              ) : (
                                <div style={{ textAlign: "center", color: "#c7cfda" }}>
                                  <div style={{ fontSize: 24, marginBottom: 8 }}>📷</div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 900,
                                      letterSpacing: 1,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Subir fotografía
                                  </div>
                                  <div style={{ fontSize: 11, opacity: 0.65, marginTop: 5 }}>
                                    Nombre, apellido y carnet
                                  </div>
                                </div>
                              )}
                            </label>

                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                marginTop: 12,
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => leerDocumentoReceptorConIA(idx, "frente")}
                                disabled={!ocrReceptorFrente.file || ocrReceptorFrente.leyendo}
                                style={{
                                  flex: "1 1 140px",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  fontSize: 11,
                                }}
                              >
                                {ocrReceptorFrente.leyendo ? "Leyendo..." : "Leer identidad"}
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-light"
                                onClick={() => limpiarDocumentoReceptor(idx, "frente")}
                                disabled={!ocrReceptorFrente.file && !ocrReceptorFrente.previewUrl}
                                style={{
                                  flex: "1 1 140px",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  fontSize: 11,
                                }}
                              >
                                Limpiar
                              </button>
                            </div>

                            {ocrReceptorFrente.datos && (
                              <div
                                style={{
                                  marginTop: 12,
                                  background: "#143425",
                                  border: "1px solid #2c7a55",
                                  color: "#d8ffe9",
                                  borderRadius: 8,
                                  padding: "9px 11px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                Frente leído. Revisá nombre, apellido y carnet.
                              </div>
                            )}
                          </div>

                          {/* DORSO RECEPTOR */}
                          <div
                            style={{
                              background: "#111722",
                              border: "1px solid #38445a",
                              borderRadius: 12,
                              padding: 16,
                            }}
                          >
                            <div
                              style={{
                                color: "#cbd3df",
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: 0.8,
                                textTransform: "uppercase",
                                marginBottom: 12,
                              }}
                            >
                              ID receptor (reverso)
                            </div>

                            <label
                              style={{
                                minHeight: 130,
                                border: "1px dashed #9b5b2c",
                                borderRadius: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                background: "#151d2a",
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => seleccionarDocumentoReceptor(idx, "dorso", e)}
                                style={{ display: "none" }}
                              />

                              {ocrReceptorDorso.previewUrl ? (
                                <img
                                  src={ocrReceptorDorso.previewUrl}
                                  alt="Dorso documento receptor"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: 120,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                  }}
                                />
                              ) : (
                                <div style={{ textAlign: "center", color: "#c7cfda" }}>
                                  <div style={{ fontSize: 24, marginBottom: 8 }}>📷</div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 900,
                                      letterSpacing: 1,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Subir fotografía
                                  </div>
                                  <div style={{ fontSize: 11, opacity: 0.65, marginTop: 5 }}>
                                    Dirección Cuba
                                  </div>
                                </div>
                              )}
                            </label>

                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                marginTop: 12,
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => leerDocumentoReceptorConIA(idx, "dorso")}
                                disabled={!ocrReceptorDorso.file || ocrReceptorDorso.leyendo}
                                style={{
                                  flex: "1 1 140px",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  fontSize: 11,
                                }}
                              >
                                {ocrReceptorDorso.leyendo ? "Leyendo..." : "Leer dirección"}
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-light"
                                onClick={() => limpiarDocumentoReceptor(idx, "dorso")}
                                disabled={!ocrReceptorDorso.file && !ocrReceptorDorso.previewUrl}
                                style={{
                                  flex: "1 1 140px",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  fontSize: 11,
                                }}
                              >
                                Limpiar
                              </button>
                            </div>

                            {ocrReceptorDorso.datos && (
                              <div
                                style={{
                                  marginTop: 12,
                                  background: "#143425",
                                  border: "1px solid #2c7a55",
                                  color: "#d8ffe9",
                                  borderRadius: 8,
                                  padding: "9px 11px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                Dorso leído. Revisá dirección, provincia y municipio.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>


                      <div className="row g-2">
                        {/* <div className="col-md-2">
                          <label className="form-label">Código</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={receptor.codrecep}
                            placeholder="Auto"
                            disabled
                          />
                        </div> */}

                        <div className="col-md-3">
                          <label className="form-label">
                            Carnet de identidad *
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.carnetIdentidad}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "carnetIdentidad",
                                e.target.value
                              )
                            }
                            placeholder="Carnet de identidad"
                            autoComplete="off"
                            disabled={saving}
                            maxLength={11}
                            inputMode="numeric"
                          />
                        </div>

                        <div className="col-md-3">
                          <div className="vr-phone-highlight">
                            <label className="form-label mb-1">
                              Teléfono Principal *
                            </label>

                            <div className="vr-phone-input">
                              <span className="vr-phone-icon" aria-hidden="true">
                                📞
                              </span>

                              <input
                                type="text"
                                className="form-control vr-phone-control"
                                value={receptor.telefonoPrincipal}
                                onChange={(e) =>
                                  handleReceptorChange(
                                    idx,
                                    "telefonoPrincipal",
                                    e.target.value
                                  )
                                }
                                placeholder="+5355531234"
                                autoComplete="off"
                                disabled={saving}
                                maxLength={11}
                                inputMode="numeric"
                              />
                            </div>
                          </div>
                        </div>
                       
{/* 
                        <div className="col-md-3">
                          <label className="form-label">
                            Correo electrónico
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            value={receptor.email}
                            onChange={(e) =>
                              handleReceptorChange(idx, "email", e.target.value)
                            }
                            placeholder="correo@email.com"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div> */}

                        <div className="col-md-3">
                          <label className="form-label">Nombre *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.nombre}
                            onChange={(e) =>
                              handleReceptorChange(idx, "nombre", e.target.value)
                            }
                            placeholder="First Name"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Segundo nombre</label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.segundoNombre}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "segundoNombre",
                                e.target.value
                              )
                            }
                            placeholder="Middle Name"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Apellido 1 *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.apellido1}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "apellido1",
                                e.target.value
                              )
                            }
                            placeholder="Last Name"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Apellido 2</label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.apellido2}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "apellido2",
                                e.target.value
                              )
                            }
                            placeholder="Segundo apellido"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">Nombre completo</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={calculado.nombreCompleto || ""}
                            readOnly
                            placeholder="Se arma automáticamente"
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">Dirección *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.direccion}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "direccion",
                                e.target.value
                              )
                            }
                            placeholder="Calle, número, casa, apto, edificio, reparto/barrio"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div>

                        {/* <div className="col-md-5">
                          <label className="form-label">
                            Referencia / Entre calles
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.referenciaDireccion}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "referenciaDireccion",
                                e.target.value
                              )
                            }
                            placeholder="Entre calles, cerca de, color de casa, referencia"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div> */}

                        <div className="col-md-3">
                          <label className="form-label">Provincia *</label>
                          <select
                            className="form-control"
                            value={receptor.provincia}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "provincia",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          >
                            <option value="">Elija provincia</option>

                            {CUBA_PROVINCIAS.map((provincia) => (
                              <option key={provincia} value={provincia}>
                                {provincia}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* <div className="col-md-3">
                          <label className="form-label">Zona logística</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={calculado.zonaLogistica || ""}
                            readOnly
                            placeholder="Automática"
                          />
                        </div> */}

                        <div className="col-md-3">
                          <label className="form-label">Municipio *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={receptor.municipio}
                            onChange={(e) =>
                              handleReceptorChange(
                                idx,
                                "municipio",
                                e.target.value
                              )
                            }
                            placeholder="Municipio"
                            autoComplete="off"
                            disabled={saving}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">País</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={receptor.pais || "CUBA"}
                            readOnly
                            placeholder="CUBA"
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">
                            Dirección completa
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={calculado.direccionCompleta || ""}
                            readOnly
                            placeholder="Se arma automáticamente"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>

            </div>
          )}

          {pasoActivo === 3 && (
            <div className="vr-step-panel">
              {/* SECCIÓN 3 - VENTA */}
          <div
            className="card mb-3"
            style={{
              backgroundColor: "#fff7ed",
              border: "1px solid #ffd9b3",
            }}
          >
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ backgroundColor: "#ffe8cc", fontWeight: 600 }}
            >
              <strong>3. Elegir Productos</strong>

              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={agregarItem}
                disabled={saving}
              >
                Agregar producto
              </button>
            </div>

            <div className="card-body">
              <div className="row g-2 mb-3">
                <div className="col-md-2">
                  <label className="form-label">Fecha</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fecha"
                    value={venta.fecha}
                    onChange={handleVenta}
                    disabled={saving}
                  />
                </div>

                {/* <div className="col-md-10">
                  <label className="form-label">Observaciones generales</label>
                  <textarea
                    className="form-control"
                    name="observaciones"
                    value={venta.observaciones}
                    onChange={handleVenta}
                    rows="2"
                    placeholder="Observaciones de la venta"
                    disabled={saving}
                  />
                </div> */}
              </div>

              {items.map((item, idx) => {
                const producto = productosMap[item.producto] || {};

                const usaColor = !!producto?.usaColor;

                const coloresDisponibles = Array.isArray(
                  producto?.coloresDisponibles
                )
                  ? producto.coloresDisponibles
                  : [];

                const cantidad = toNumber(item.cantidad, 0);
                const precio = toNumber(item.precioVentaUSD, 0);
                const envio = toNumber(item.costoEnvioUSD, 0);
                const subtotal = cantidad * precio + cantidad * envio;

                return (
                  <div className="card mb-3" key={item.tempId}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <strong>Ítem {idx + 1}</strong>

                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => quitarItem(idx)}
                        disabled={saving || items.length <= 1}
                      >
                        Quitar
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="row g-2">
                        <div className="col-md-5">
                          <label className="form-label">
                            Producto *{" "}
                            <span className="vr-product-emojis">
                              🏍️ ⛑️ 📺 🔋
                            </span>
                          </label>

                          <div className="vr-product-picker">
                            <div className="vr-product-picker-row">
                              <input
                                type="text"
                                className="form-control vr-product-search-input"
                                value={getProductoInputValue(item)}
                                onChange={(e) =>
                                  handleBuscarProductoItem(item.tempId, e.target.value)
                                }
                                onFocus={() => setProductoPickerActivo(item.tempId)}
                                disabled={saving || loadingProductos}
                                placeholder={
                                  loadingProductos
                                    ? "Cargando productos..."
                                    : "Buscar por código, nombre, modelo o marca"
                                }
                                autoComplete="off"
                              />

                              {item.producto && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger vr-product-clear-btn"
                                  onClick={() => limpiarProductoItem(idx, item)}
                                  disabled={saving}
                                  title="Cambiar producto"
                                >
                                  ×
                                </button>
                              )}
                            </div>

                            {productoPickerActivo === item.tempId && (
                              <div className="vr-product-results">
                                {productosParaItem(item).length === 0 ? (
                                  <div className="vr-product-result-empty">
                                    No se encontraron productos.
                                  </div>
                                ) : (
                                  productosParaItem(item).map((prod) => {
                                    const id = getId(prod);

                                    return (
                                      <button
                                        key={id}
                                        type="button"
                                        className={`vr-product-result ${
                                          item.producto === id ? "selected" : ""
                                        }`}
                                        onClick={() => seleccionarProductoItem(idx, item, id)}
                                        disabled={saving}
                                      >
                                        <span className="vr-product-result-name">
                                          {prod.codprod ? `${prod.codprod} - ` : ""}
                                          {nombreProducto(prod)}
                                        </span>

                                        <span className="vr-product-result-price">
                                          {prod.precioVentaUSD
                                            ? formatUSD(prod.precioVentaUSD)
                                            : ""}
                                        </span>
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-md-4">
                          {estadoRecepcion === "COMPLETA" ? (
                            <>
                              <label className="form-label">
                                Receptor destino *
                              </label>

                              <select
                                className="form-control"
                                value={item.receptorKey}
                                onChange={(e) =>
                                  handleItemChange(
                                    idx,
                                    "receptorKey",
                                    e.target.value
                                  )
                                }
                                disabled={saving}
                              >
                                <option value="">Elija receptor</option>

                                {receptoresCalculados.map(
                                  (receptor, rIdx) => (
                                    <option
                                      key={receptor.tempId}
                                      value={receptor.tempId}
                                    >
                                      {nombreReceptorSelect(
                                        receptor,
                                        rIdx
                                      )}
                                      {receptor.carnetIdentidad
                                        ? ` - ${upper(
                                            receptor.carnetIdentidad
                                          )}`
                                        : ""}
                                    </option>
                                  )
                                )}
                              </select>
                            </>
                          ) : (
                            <>
                              <label className="form-label">
                                Receptor destino
                              </label>

                              <div className="alert alert-warning py-2 mb-0">
                                ⏳ Recepción pendiente — se completará
                                posteriormente.
                              </div>
                            </>
                          )}
                        </div>
                        
                        <div className="col-md-1">
                          <label className="form-label">Cant.</label>
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            value={item.cantidad}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "cantidad",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label">Precio USD</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            value={item.precioVentaUSD}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "precioVentaUSD",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          />
                        </div>

                        {/* <div className="col-md-2">
                          <label className="form-label">Envío USD</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            value={item.costoEnvioUSD}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "costoEnvioUSD",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          />
                        </div> */}

                        <div className="col-md-3">
                          <label className="form-label">Subtotal</label>
                          <input
                            className="form-control bg-light"
                            value={formatUSD(subtotal)}
                            readOnly
                          />
                        </div>

                        {usaColor && (
                          <div className="col-md-12">
                            <label className="form-label">
                              Colores preferidos
                            </label>
                            <div className="vr-colores-box border rounded p-3">
                              {coloresDisponibles.length === 0 ? (
                                <div className="text-muted">
                                  El producto utiliza colores, pero no tiene colores disponibles
                                  cargados.
                                </div>
                              ) : (
                                <div className="row g-2">
                                  {coloresDisponibles.map((color) => {
                                    const colorId = getColorId(color);

                                    const seleccionado = Array.isArray(
                                      item.coloresPreferidos
                                    )
                                      ? item.coloresPreferidos.includes(colorId)
                                      : false;

                                    return (
                                      <div
                                        className="col-6 col-md-3"
                                        key={colorId}
                                      >
                                        <label
                                          className={`vr-color-option d-flex align-items-center gap-2 border rounded px-2 py-2 ${
                                            seleccionado ? "selected" : ""
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={seleccionado}
                                            onChange={() =>
                                              toggleColorPreferido(idx, colorId)
                                            }
                                            disabled={saving}
                                          />

                                          <span className="vr-color-name fw-semibold">
                                            {getColorNombre(color)}
                                          </span>
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {usaColor && (
                          <div className="col-md-12">
                            <label className="form-label">
                              Observación de color
                            </label>

                            <input
                              type="text"
                              className="form-control"
                              value={item.observacionColor || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "observacionColor",
                                  e.target.value
                                )
                              }
                              autoComplete="off"
                              disabled={saving}
                              placeholder="Ejemplo: primera opción negro, segunda opción rojo"
                            />
                          </div>
                        )}

                        <div className="col-md-7">
                          <label className="form-label">
                            Observaciones del ítem
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={item.observaciones}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "observaciones",
                                e.target.value
                              )
                            }
                            autoComplete="off"
                            disabled={saving}
                            placeholder="Ejemplo: accesorios, cascos, tv, baterias"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="card mt-4">
                <div className="card-header">
                  <strong>Resumen</strong>
                </div>

                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-md-4">
                      <div className="vr-resumen-producto border rounded p-2">
                        <div className="text-muted small">
                          Producto
                        </div>

                        <strong>
                          {formatUSD(totales.totalProductosUSD)}
                        </strong>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="vr-resumen-tax border rounded p-2">
                        <div className="small">
                          Tax {totales.taxPorcentaje}%
                        </div>

                        <strong>
                          {formatUSD(totales.taxUSD)}
                        </strong>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="vr-resumen-total border rounded p-2">
                        <div className="small">
                          Total a cobrar
                        </div>

                        <strong>
                          {formatUSD(totales.totalUSD)}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

            </div>
          )}

          {/* BOTONES */}
          <div className="vr-actions">
            <div className="vr-actions-note">
              Podés volver a pasos anteriores para corregir datos antes de guardar.
            </div>

            <div className="vr-actions-buttons">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={limpiarFormulario}
                disabled={saving}
              >
                Limpiar
              </button>

              <button
                type="button"
                className="btn btn-outline-light"
                onClick={irPasoAnterior}
                disabled={saving || pasoActivo === 1}
              >
                ← Anterior
              </button>

              {pasoActivo < 4 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={irPasoSiguiente}
                  disabled={saving}
                >
                  Siguiente →
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={saving || resumenPagosIniciales.totalPagosUSD <= 0}
                  title={
                    resumenPagosIniciales.totalPagosUSD <= 0
                      ? "Debe ingresar al menos un pago"
                      : "Guardar venta rápida"
                  }
                >
                  {saving ? "Guardando..." : "Guardar venta rápida"}
                </button>
              )}
            </div>
          </div>

          {/* WhatsApp desactivado por ahora en Venta Rápida.
              Se enviará desde Listado de Ventas / app principal. */}
          {false && ventaWhatsappPendiente && (
            <div className="alert alert-success mt-3 d-flex justify-content-between align-items-center">
              <div>
                <strong>Venta guardada correctamente.</strong>
                <div className="small">
                  Ya podés enviar los datos por WhatsApp al receptor.
                </div>
              </div>

              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={() => abrirWhatsAppVenta(ventaWhatsappPendiente)}
              >
                Enviar WhatsApp
              </button>
            </div>
          )}

        </div>
      </div>
    </form>

    {showCalculadoraPaso4 && (

      <div
        className="cr-integracion-overlay"
        onMouseDown={() =>
          setShowCalculadoraPaso4(
            false
          )
        }
      >

        <div
          className="cr-integracion-modal"
          onMouseDown={(
            e
          ) =>
            e.stopPropagation()
          }
        >

          <CalculadoraRiders
            onCerrar={() =>
              setShowCalculadoraPaso4(
                false
              )
            }

            onAplicar={
              aplicarCalculadoraPaso4
            }

            financierasExternas={
              financieras
            }

            saldoPendientePaso4USD={
              resumenPagosIniciales
                .saldoPendienteUSD
            }

            saldoPendienteSinTaxPaso4USD={
              saldoPendienteSinTaxUSD
            }

            usarSaldoAutomaticoPaso4={
              true
            }
          />

        </div>

      </div>

    )}

    {modalVentaOk.show && (
      <div className="vr-success-overlay">
        <div className="vr-success-modal">
          <div className="vr-success-emoji">😊</div>

          <h3>Se grabó su venta</h3>

          {modalVentaOk.nro && (
            <div className="vr-success-nro">
              Venta{modalVentaOk.nro}
            </div>
          )}

          <button
            type="button"
            className="btn btn-success vr-success-btn"
            onClick={cerrarModalVentaOk}
          >
            OK
          </button>

          <div className="vr-success-note">
            Recordá que la misma persona no puede recibir 2 productos de combustión
            durante 5 años.
          </div>

          {modalVentaOk.warning && (
            <div className="vr-success-warning">
              {modalVentaOk.warning}
            </div>
          )}
        </div>
      </div>
    )}

    <ModalGaranteFinanciacion
      show={modalGaranteFinanciacion.show}
      onClose={cerrarModalGaranteFinanciacion}
      onSaved={onGaranteFinanciacionGuardado}
    />

    <ModalDocumentoCropper
      show={showCropFrente}
      file={ocrFrenteOriginalFile}
      titulo="Editor frente documento comprador"
      subtitulo="Ajustá el área del documento. Al guardar se leerá con IA."
      onCancel={cancelarCropFrente}
      onSave={guardarFrenteRecortado}
    />

    <ModalDocumentoCropper
      show={showCropDorso}
      file={ocrDorsoOriginalFile}
      titulo="Editor reverso documento comprador"
      subtitulo="Ajustá el área del domicilio. Al guardar se leerá con IA."
      onCancel={cancelarCropDorso}
      onSave={guardarDorsoRecortado}
    />

    <ModalDocumentoCropper
      show={cropReceptor.show}
      file={cropReceptor.file}
      titulo={
        cropReceptor.tipo === "frente"
          ? "Editor frente documento receptor"
          : "Editor reverso documento receptor"
      }
      subtitulo={
        cropReceptor.tipo === "frente"
          ? "Ajustá el área de identidad. Al guardar se leerá con IA."
          : "Ajustá el área de domicilio. Al guardar se leerá con IA."
      }
      onCancel={cancelarCropReceptor}
      onSave={guardarReceptorRecortado}
    />

   </> 
  );

};

export default AddVentaRapidaForm;